/*
 * tool-usage-limit.js — reusable client-side SOFT daily usage limit for
 * browser-only tools, as a gentle Pro funnel.
 *
 * HONEST SCOPE: this is tier separation, NOT DRM. Browser tools do all work
 * client-side (Web Audio / WASM) with no server touchpoint to hard-gate, so the
 * counter lives in localStorage and a determined user can reset it (clear storage,
 * incognito). Its job is (a) a soft cap on casual over-use and (b) an upgrade
 * prompt — not enforcement. Same philosophy as the TTS HD-permit ("a JS-savvy
 * user can skip it — that's product-tier separation, not DRM").
 *
 * CAP SOURCE: window.__tierLimits.tool_daily[slug], server-resolved per tier
 * (config/tiers.php → User::tierLimits → session-bootstrap; anon via the layout
 * fallback keyed on window.__PRO_HERE). Caps apply wherever Pro is SOLD (RU
 * YooKassa ∪ intl Polar); on locales without a Pro offer the server/fallback
 * hand out the Pro ceiling — no paywall friction where there's no upsell.
 * Pro users get the raised cap. A missing / <=0 cap means UNLIMITED (fail-open).
 * Build-time guard against a missing key: tests/Unit/ToolDailyCapMirrorTest.
 *
 * ONE exception to fail-open (2026-07-28): when the snapshot carries
 * caps_blocked = true — an account whose email is NOT verified — a cap of 0 means
 * CLOSED. The flag exists because a bare 0 has always meant "unconfigured" here,
 * so the server sends both and this module honours the zero only with the flag.
 * Such a user sees the wall on arrival (no press needed) and cannot start a run.
 * Their TOKEN-paid lanes are unaffected: token-priced tools carry no daily cap at
 * all, and the mixed free+paid tools gate only their free lane through begin().
 *
 * ── HOW TO USE (2026-07-08 onwards) ────────────────────────────────────────
 *
 *   var run = window.__toolLimit.begin(slug);   // null ⇒ blocked (note shown for you)
 *   if (!run) return;
 *   try { ...the expensive work... run.commit(); }   // exactly ONE record per token
 *   catch (e) { run.abort(); }                       // cancelled/failed ⇒ never counted
 *
 * A result that waits to be TAKEN (a «Скачать» only a press drives, a share
 * sheet that needs a tap) is not a delivered one yet:
 *
 *   run.awaitDelivery();   // the work is over: the run bracket closes now
 *   …the file reaches the person → run.commit(); thrown away → run.abort()
 *
 * The unit stays unspent until then, and a result nobody took costs nothing
 * («списывает только ВЫДАННЫЙ результат» — /slideshow-maker, bug report
 * 16.10.2026: a test export the person never downloaded spent the day's one
 * slideshow, and the wall then said «уже сохранено» about a file they never had).
 *
 * commit() is IDEMPOTENT and abort() is FINAL: once aborted, a late commit()
 * from an unabortable worker is a no-op. That is the whole point — the previous
 * allowed()/record() pair left the two halves far apart in the async flow, and
 * every tool wired them differently. Measured consequences before this API:
 *   gif-compressor  — Cancel still billed a run (handleError reset its own flag)
 *   harmony-generator — billed per DOWNLOAD, so 2 renders cost 0 and 1 render
 *                       exported twice cost 2
 *   speech-to-text  — 3 entry points reached the work past the gate
 *
 * A token cannot fix a NEW entry point that simply never calls begin(). For that,
 * funnel every entry point through one function that takes the token.
 *
 * UI: mount(slug, {...}) owns the "N of M left" hint and the "limit reached →
 * Pro" note — including hiding both for Pro/unlimited users, re-hiding the note
 * once a run succeeds, and re-rendering when window.__tierLimits finally arrives
 * (it is set by an async fetch; see the tier-bootstrap:ready listener below).
 * The <x-tool-limit> Blade component emits the markup and auto-mounts, so a tool
 * writes ZERO limit UI code. Legacy tools can pass their own element ids.
 *
 * The old allowed()/record() pair is still exported: slideshow-app.js and other
 * un-migrated call sites use it, and record() is what begin() builds on.
 */
(function () {
    'use strict';
    /* «День» счётчика — от СЕРВЕРНЫХ часов, как только они известны
       (2026-08-05). Раньше день брался прямо из часов устройства, и
       фольклорный трюк «переведи дату в настройках — счётчик обнулится»
       работал буквально. Снапшот тира приносит server_now (epoch мс,
       /api/session-bootstrap, гостям тоже); запоминаем оффсет к Date.now()
       и считаем день по скорректированному времени — перезагрузка с любым
       положением системных часов даёт тот же серверный день, и счётчик
       остаётся на месте.
       Именно ОФФСЕТ, а не замороженная строка дня: полуночный ролловер в
       долгоживущей вкладке обязан работать (стена сама поднимается в новый
       день), а база Date.now() переживает сон ноутбука — monotonic
       performance.now() после suspend отстаёт на всю длительность сна.
       Без снапшота (офлайн-PWA, недоступный бутстрап) — часы клиента, как
       раньше: fail-open в духе всего модуля, см. HONEST SCOPE в шапке.
       Граница дня остаётся ЛОКАЛЬНОЙ полуночью (toDateString в поясе
       устройства): серверные часы чинят значение времени, не пояс. */
    var srvOffsetMs = null, srvSeenNow = 0;
    function syncServerClock() {
        try {
            var sn = window.__tierLimits && window.__tierLimits.server_now;
            if (typeof sn === 'number' && sn > 0 && sn !== srvSeenNow) {
                srvSeenNow = sn;
                srvOffsetMs = sn - Date.now();
            }
        } catch (_) {}
    }
    /* Membership test for key batches. Set when the engine has one; otherwise a
       null-prototype object, so a key literally named "constructor" or
       "__proto__" cannot answer `true` by inheritance. */
    /* A file is the same file by its name and exact size; its lastModified is not
       compared. The key keeps the form name|size|lastModified (AudioParts maps a
       part to its recording by that string), and every comparison drops the date —
       in a single key and in each part of a composite one ('sel:a~b', 'set:a,b').
       Why: on Android the gallery picker stamps lastModified with the moment of the
       PICK, so every re-pick of one video was a new file and billed again (support
       mail 16.10.2026, /enhance-video: the same 243 926 981-byte video, a wall on
       three page loads of one day). Measured on RU repeats 16–17.10 (a re-pick on a
       new page, outcome known): ignoring the date keeps 127 of 128 recognised
       repeats and recovers 10 of the 16 walls caused by the key; ignoring it only
       when it looks stamped breaks 24 of 128 (a file just made or downloaded comes
       back on the next page with its real date). The price: a same-name file
       re-saved to the same byte length counts as the file it replaced. Stored keys
       of the old form compare the same way, so nothing billed earlier today is
       billed again. */
    function canonKey(k) {
        return typeof k === 'string' ? k.replace(/\|(\d+)\|(\d+)(?=$|[~,])/g, '|$1') : k;
    }
    function makeKeySet(list) {
        var n = list ? list.length : 0, i;
        if (typeof Set === 'function') {
            try {
                var set = new Set();
                for (i = 0; i < n; i++) set.add(canonKey(list[i]));
                return {
                    has: function (k) { return set.has(canonKey(k)); },
                    add: function (k) { set.add(canonKey(k)); }
                };
            } catch (_) { /* fall through */ }
        }
        var bag = Object.create(null);
        for (i = 0; i < n; i++) bag['k' + canonKey(list[i])] = 1;
        return {
            has: function (k) { return bag['k' + canonKey(k)] === 1; },
            add: function (k) { bag['k' + canonKey(k)] = 1; }
        };
    }

    function dayStamp() { try { return new Date(Date.now() + (srvOffsetMs || 0)).toDateString(); } catch (_) { return 'day'; } }

    /* Files THIS page load has already paid for (or re-ran free), per slug.
       The day's list (state.f) resets at local midnight, and until 2026-09-25
       that was the whole memory: a recording loaded at 23:40 and re-run with
       another preset at 00:55 was billed again although nothing new had come
       in — feedback 2026-09-19 (/speech-restorer, sid 3d3b071d: first run 19:24
       UTC, repeats free, the repeat after local midnight took a unit, the next
       part hit the wall). The unit is a file; the same loaded file stays paid
       for as long as the page holds it. A reload starts from the day's list
       again, so this never outlives the tab. */
    var pagePaid = {};
    function pageKeys(slug) { return pagePaid[slug] || (pagePaid[slug] = makeKeySet([])); }
    function key(slug) { return 'tul:' + slug; }

    /* Очереди, про которые precheck судил ДО прихода снимка тиров, — то есть
       заведомо мимо. Переигрываются один раз на tier-bootstrap:ready (см.
       комментарий в precheck). Ссылки на File здесь те же, что держит сам тул,
       копий не делаем; после переигровки запись снимается. */
    var pendingPrecheck = {};

    function readState(slug) {
        try {
            var raw = localStorage.getItem(key(slug));
            if (raw) { var o = JSON.parse(raw); if (o && o.d === dayStamp() && typeof o.n === 'number') return o; }
        } catch (_) {}
        return { d: dayStamp(), n: 0 };
    }

    /* Unverified account (email not confirmed) — EVERY capped tool is closed
       (Farid 2026-07-28). The server sends tool_daily[slug] = 0 for those users
       AND caps_blocked = true, because a bare 0 has always meant "no cap
       configured → fail open" here (and still does for guests on a locale that
       sells no plan). Only the FLAG turns a zero into a wall, so a config typo
       can never silently uncap the tier. A slug absent from the map stays
       uncapped for everyone: only tools that HAVE a cap are closed.
       Token-paid lanes never route through this module (see
       ToolDailyCapMirrorTest::test_paid_token_tools_stay_out_of_the_daily_run_cap),
       so a user who bought tokens keeps spending them. */
    /* A paid BYO-Browser work order (?tmb_run=…, stamped by the layout as
       data-api-mode on <body>). The run was already paid for through the API
       wallet, so the free-tier daily cap must not also apply — double-charging
       an integrator once in tokens and once in refusals is not a tier, it is a
       bug. Lifting the cap is in fact part of what the API key buys. */
    function apiMode() {
        return !!(document.body && document.body.getAttribute('data-api-mode') === '1');
    }

    /* ── Снимок тиров ЕЩЁ В ПУТИ ─────────────────────────────────────────────
       window.__tierLimits приезжает только с асинхронным /api/session-bootstrap.
       До этого cap() не видел ни одного числа и отвечал Infinity, то есть
       begin() пропускал ЛЮБОЙ прогон. Замер 12.09.2026 на /photo-translate:
       второй заход гостя, через 2 с после загрузки `cap = null`, в счётчике
       уже 2 из 2 — и пачка из двух новых снимков ушла в работу. Окно не
       теоретическое: оно длится ровно столько, сколько идёт ответ bootstrap
       (на проде 1.7–3.3 с, см. precheck), а файл бросают, как только видят зону.

       Для ГОСТЯ ответ известен заранее: layout держит его в __anonTierLimits() —
       ту же гостевую карту из config/tiers.php, которую bootstrap гостю и
       вернёт (ветка гостя там буквально `: __anonTierLimits()`). Поэтому, пока
       настоящего снимка нет, гость судится гостевыми числами — теми, что и так
       увидит через секунду.

       Вошедшего так судить НЕЛЬЗЯ: у Премиума кап 1000, и гостевые 2 заперли бы
       платящего человека стеной, которой у него нет. При признаке входа
       (разметка data-authed или кука-метка timbrica_auth, которую продлевает сам
       bootstrap) поведение прежнее: ждём настоящий снимок и до него не запираем.
       Дыра остаётся только у вошедшего бесплатного аккаунта и только на эти
       секунды — а его кап и так не выше гостевого. Стену, поднятую гостевыми
       числами тому, кто пришёл без метки, пересуживает rejudgeProvisionalWalls(),
       когда снимок приедет. */
    var provisionalTiers = null;

    function authMarked() {
        try {
            if (document.body && document.body.getAttribute('data-authed') === '1') return true;
            return /(^|;\s*)timbrica_auth=/.test(String(document.cookie || ''));
        } catch (_) { return false; }
    }

    /** Снимок, по которому кап судится ПРЯМО СЕЙЧАС, или null — судить нечем. */
    function tiers() {
        if (window.__tierLimits) return window.__tierLimits;
        if (authMarked()) return null;
        // Запоминается только УДАЧА (снимок зависит лишь от локали страницы):
        // вызов раньше, чем layout объявил помощника, спросит снова, а не
        // застынет на «нечем» до самого bootstrap.
        if (!provisionalTiers) {
            try {
                if (typeof window.__anonTierLimits === 'function') provisionalTiers = window.__anonTierLimits() || null;
            } catch (_) { provisionalTiers = null; }
        }
        return provisionalTiers;
    }

    /* ── A cap the SERVER counts ─────────────────────────────────────────────
       For almost every tool the unit is spent in this page and counted here.
       Some are spent on OUR server — a page of somebody else's site fetched for
       /anglicism-checker or /signage-law-check, one count for both tools
       (App\Services\ForeignPages\ForeignPageQuota, Фарид 22.10.2026) — and
       there the server's answer IS the count. The page hands every answer over
       (serverCount) and its refusal (serverRefused); the block it gets back is
       the one every capped tool shows — counter, bar, the wall with the plan's
       number and the bold spent line, the dimmed button. Such a slug keeps
       nothing in localStorage, and begin() is not used for it: the server has
       already decided. */
    var serverCounts = Object.create(null);

    function usedOf(slug) {
        var sc = serverCounts[slug];
        return sc ? sc.used : readState(slug).n;
    }

    function isBlocked(slug) {
        if (serverCounts[slug]) return false;   // the server answers for every tier itself
        if (apiMode()) return false;
        var tl = tiers() || {};
        return !!tl.caps_blocked && typeof (tl.tool_daily || {})[slug] === 'number';
    }

    function cap(slug) {
        var sc = serverCounts[slug];
        if (sc) return sc.cap > 0 ? sc.cap : Infinity;
        if (apiMode()) return Infinity;
        var tl = tiers() || {};
        var map = tl.tool_daily || {};
        var c = map[slug];
        if (typeof c !== 'number') return Infinity;             // unconfigured → fail-open
        if (tl.caps_blocked) return 0;                          // unverified account → closed
        if (c <= 0) return Infinity;                            // unlimited (no plan sold here) → fail-open
        return c;
    }

    /* ── mounted UI, keyed by slug ───────────────────────────────────────────
       A slug may own SEVERAL notes/hints: music-mastering shows one in the
       result panel and another over its batch queue. Both must react to the
       same cap, so a mount is a list, not a single element. */
    var mounts = Object.create(null);

    function byId(v) { return typeof v === 'string' ? document.getElementById(v) : (v || null); }

    function hide(el) { if (el) { el.hidden = true; el.style.display = 'none'; } }
    function show(el) { if (el) { el.hidden = false; el.style.display = ''; } }

    function slot(slug) {
        return mounts[slug] || (mounts[slug] = { notes: [], hints: [], roots: [], buttons: [], autoButtons: [], noteVisible: false });
    }

    /* ── WHICH button was pressed — observed, not declared ────────────────────
       `data-tl-button` has always been OPT-IN, and opting in is a rule that
       decays: measured 2026-08-31, 218 mounts across the site and only 62 name
       a button at all. On the other 156 a refused press changes nothing on the
       thing the hand is actually on.

       The press itself is knowable without any declaration. A capture-phase
       listener runs BEFORE the tool's own handler, so when begin() is called
       from inside that handler — synchronously, or across microtasks, which is
       what `sync` tracks — the element under the pointer is certainly the
       button being refused. No freshness heuristic, no guessing: if the flag is
       not set we simply do not paint, and the escalation below still answers.

       Declaring stays worthwhile (it also covers presses that reach begin()
       after a real await), but it is no longer the difference between feedback
       and silence. */
    var press = { el: null, sync: false };
    try {
        document.addEventListener('click', function (ev) {
            var t = ev.target;
            var el = (t && t.closest) ? t.closest('button, [role="button"], input[type="submit"], input[type="button"]') : null;
            // The wall's own CTA is not a tool action — painting it would grey
            // out the way OUT of the wall.
            if (el && el.closest && el.closest('[data-tool-limit]')) el = null;
            press.el = el;
            press.sync = true;
            // Cleared once the current task (the tool's handler and everything
            // its microtasks chain onto it) has drained.
            setTimeout(function () { press.sync = false; press.el = null; }, 0);
        }, true);
    } catch (_) {}

    /* ── begin() with no key: bill the MATERIAL, not the press ───────────────
       «Повтор того же файла в тот же день — бесплатно» is not an internal nicety:
       it is printed on the wall itself, in ten locales, and it is what makes a
       cap of 2 workable on a tool people iterate with. It is implemented by the
       key — and a call that passes none bills every press instead.

       MEASURED 2026-08-31 over 30 days: **32 capped tools with 40+ walls emitted
       ZERO `limit_free_rerun`** — not one recognised repeat between them, on
       volumes like 1460 walls (/extract-audio) and 969 (/mute-video). They pass
       no key. So on those tools the printed promise was simply not implemented,
       and «2 файла в сутки» actually meant «2 нажатия в сутки».

       The page already knows what is loaded: the file input holds the ORIGINAL
       picked File, which is also the identity a tool cannot lose by rewriting
       its own working copy. So a keyless begin() falls back to it.

       Deliberately ONE composite key over the whole selection, never an array:
         · cost is unchanged — one press still spends exactly one unit, so no
           tool can start charging more than it does today;
         · a second press on the same selection becomes free, which is the whole
           promise;
         · a tool whose unit is genuinely a composition (/split-screen) is not
           re-interpreted as per-file behind its back.
       Tools that pass their own key are untouched. Generators have no input with
       files, so they keep billing the press — which is correct: there the press
       IS the work. */
    /* ⚠️ The <input> cannot be that source on its own: the shared uploader
       CLEARS input.value right after dispatching, on purpose, so that re-picking
       the same file fires `change` again (Chromium suppresses it otherwise — a
       2026-06-12 bug report where a re-picked 756 MB mp4 produced total
       silence). Reading input.files therefore finds nothing on exactly the tools
       this fallback is for. The uploader's own bubbling event is the honest
       source; the input stays as a fallback for the tools that roll their own. */
    var picked = [];   // [{el, keys}] — one entry per uploader on the page
    function rememberPick(el, files) {
        var keys = [], list = [];
        for (var i = 0; i < ((files && files.length) || 0); i++) {
            var k = API.fileKey(files[i]);
            if (k && keys.indexOf(k) < 0) keys.push(k);
            if (files[i]) list.push(files[i]);
        }
        var known = null;
        for (var j = 0; j < picked.length; j++) {
            if (picked[j].el === el) { known = picked[j]; break; }
        }
        if (known) { known.keys = keys; known.files = list; }
        else picked.push({ el: el, keys: keys, files: list });
        // The selection changed — a wall that answered a different file may no
        // longer be true (reconsiderWallAfterPick). Here, not in the binder: the
        // binder only watches the zone and is pinned to exactly that shape
        // (UploaderListenerRaceTest), and every way a pick is recorded passes
        // through this function.
        reconsiderWallAfterPick();
    }
    /* Which zone delivered this file — by identity first, then by the
       name|size|lastModified key (a tool may have swapped in the durable copy
       FileBytes.settle() returns). The in-place parts path (audio-parts.js)
       hands each part back through this zone. */
    function zoneFor(file) {
        if (!file) return null;
        var k = rawKey(file), byKey = null;
        /* The zone itself records what it handed out (el.__deliveredFiles, set
           BEFORE the event is dispatched — tool-uploader.js). That answer does
           not depend on when this module subscribed: bindUploaders() runs on
           tier-bootstrap or at 1.6 s, and a file picked before that reached the
           duration gate with the pick list still empty — the dialog then had no
           zone to cut into and offered the old handoff. */
        try {
            var zones = document.querySelectorAll('.tool-uploader');
            for (var z = 0; z < zones.length; z++) {
                var got = zones[z].__deliveredFiles || [];
                for (var g = 0; g < got.length; g++) {
                    if (got[g] === file) return zones[z];
                    if (!byKey && k && rawKey(got[g]) === k) byKey = zones[z];
                }
            }
        } catch (_) {}
        if (byKey) return byKey;
        for (var i = picked.length - 1; i >= 0; i--) {
            var p = picked[i];
            if (!p.el || !p.el.isConnected) continue;
            if (p.files && p.files.indexOf(file) >= 0) return p.el;
            if (!byKey && p.files) {
                for (var j = 0; j < p.files.length; j++) if (rawKey(p.files[j]) === k) { byKey = p.el; break; }
            }
        }
        return byKey;
    }
    function rawKey(file) {
        try { return String(file.name || '').slice(0, 80) + '|' + (file.size | 0) + '|' + (file.lastModified || 0); }
        catch (_) { return null; }
    }
    /* Through __bindUploaderFile, NOT a raw listener on the event: this module
       is loaded by the layout and can attach AFTER the user has already picked
       (slow 4G, content blocker, Service-Worker first visit). A raw listener
       misses that pick silently — and here "silently" means the fallback finds
       no selection and the tool goes back to billing every press, which is the
       exact bug this fallback exists to fix. The helper replays el.__pendingFile
       for us. Ratcheted by tests/Feature/UploaderListenerRaceTest.

       ⚠️ As an OBSERVER ({observer: true}), and that is not a detail. This
       module only wants to KNOW what was picked; the pick is for the tool. A
       plain binder is a consumer, and the helper clears the zone's stamp on
       first consume — so this module, bound on every zone and bound early,
       took the stamp away before the tool could bind and be replayed from it.
       From the end of August until 07.10.2026 that switched the early-pick
       rescue off for every tool whenever this module had bound through the
       real helper (production: 6 of 9 returning visits). */
    function bindUploaders() {
        if (typeof window.__bindUploaderFile !== 'function') return;
        var zones = document.querySelectorAll('.tool-uploader');
        for (var i = 0; i < zones.length; i++) {
            var el = zones[i];
            if (el.__tlBound) continue;
            el.__tlBound = true;
            (function (zone) {
                window.__bindUploaderFile(zone, function (f, detail) {
                    rememberPick(zone, (detail && detail.files) || (f ? [f] : []));
                }, { observer: true });
                // Clearing is not a pick, so it has no race to lose — and the
                // helper only speaks for `:file`.
                zone.addEventListener('tool-uploader:clear', function () { rememberPick(zone, []); });
            })(el);
        }
    }

    /* Стена — ответ на отказанное нажатие, и относится он к ТОМУ файлу. Человек
       упёрся в неё со вторым файлом и вернул на страницу первый, уже учтённый
       сегодня: сохранять его можно сколько угодно (begin() → бесплатный повтор),
       а на экране по-прежнему «файлы на сегодня закончились» и погашенная
       кнопка. При капе в один файл это не край, а обычный вечер: попробовал
       второй — вернулся к первому. Поэтому смена выбора на учтённый файл снимает
       стену сама, не дожидаясь нажатия в кнопку, которая выглядит мёртвой.

       Судим только там, где это можно знать наверняка: отказ был про ОДНУ
       единицу, и на странице сейчас ровно один выбор, ключ которого уже есть в
       сегодняшнем списке этого тула — в той форме, в какой тул его записал
       (ключ выбора страницы у begin без ключа, ключ файла у begin(fileKey(file))).
       Очередь из нескольких файлов (/join), составной ключ операции, ключ
       колоды — в списке под ключом выбранного файла их нет, и стена остаётся. */
    function reconsiderWallAfterPick() {
        for (var slug in mounts) {
            var m = mounts[slug];
            if (!m || !m.noteVisible || isBlocked(slug)) continue;
            if (!m.wallKeys || m.wallKeys.length !== 1) continue;
            var cand = null;
            if (m.wallKeyless) cand = selectionKey();
            else {
                var seen = [];
                for (var i = 0; i < picked.length; i++) {
                    for (var j = 0; j < picked[i].keys.length; j++) if (seen.indexOf(picked[i].keys[j]) < 0) seen.push(picked[i].keys[j]);
                }
                if (seen.length === 1) cand = seen[0];
            }
            if (!cand) continue;
            try { if (API.freshKeys(slug, [cand]).length) continue; } catch (_) { continue; }
            hideNote(slug);
            renderHint(slug);
            ae(slug, { f: 'limit_wall_lifted_on_pick' });
        }
    }

    function selectionKey() {
        try {
            var parts = [];
            var add = function (k) { if (k && parts.indexOf(k) < 0) parts.push(k); };
            for (var i = 0; i < picked.length; i++) {
                for (var j = 0; j < picked[i].keys.length; j++) add(picked[i].keys[j]);
            }
            // Tools that do not use the shared uploader keep their selection on
            // the input itself.
            var inputs = document.querySelectorAll('input[type="file"]');
            for (var a = 0; a < inputs.length; a++) {
                var fl = inputs[a].files;
                for (var b = 0; b < ((fl && fl.length) || 0); b++) add(API.fileKey(fl[b]));
            }
            if (!parts.length) return null;
            // Sorted: the same material picked in a different order is the same
            // material, and re-picking must not read as something new.
            return 'sel:' + parts.sort().join('~');
        } catch (_) { return null; }
    }

    function adoptPressedButton(slug) {
        if (!press.sync || !press.el) return;
        var m = mounts[slug];
        if (!m || !press.el.isConnected) return;
        if (m.buttons.indexOf(press.el) >= 0 || m.autoButtons.indexOf(press.el) >= 0) return;
        // Keep the list short: a tool has a handful of delivery buttons, and an
        // unbounded list would keep painting elements the user has moved on from.
        m.autoButtons.push(press.el);
        if (m.autoButtons.length > 6) m.autoButtons.shift();
    }

    /**
     * Is the user actually approaching the wall?
     *
     * ⚠️ SCOPE CHANGED 2026-07-31. This used to gate the PAID line and its CTA as
     * well, so «Премиум: до N в день» stayed hidden until the counter was nearly
     * spent. Farid ended that: «покажи сразу, пусть не скрывается».
     *
     * What killed the old rule was the threshold being a share of the FREE cap.
     * After the 100→1000 raise, a tool with 100 free runs only revealed the paid
     * number at 30 remaining — i.e. after burning 70 operations in one day. So on
     * exactly the tools with the most striking gap (100 → 1000) practically
     * nobody ever saw it. The earlier measurement (2026-07-09: 96.1% of user-days
     * never come close to 10 runs) argued against nagging people near a wall they
     * will not reach — but it was never an argument for hiding what the paid plan
     * gives.
     *
     * The function now drives ONLY the counter's `level`, i.e. the bar colour:
     * amber near the wall, red when spent. That still has to stay proportional —
     * a bar that turned amber on the first run would say nothing.
     *
     * ⚠️ 2026-09-19: «a bar that turned amber on the first run would say nothing»
     * — and that is exactly what this function did. The rule was
     * `remaining <= max(3, 30 % of cap)`, written when the smallest cap was 10.
     * After the 2026-08-20 wave the file tools went to a cap of 2, so an
     * UNTOUCHED counter («осталось 2 из 2») satisfied it from the first visit:
     * a full amber bar on 198 of the 237 capped tools, read from live pages.
     * An untouched counter is never «near the wall»; near it means at most
     * 30 % left, and always the last unit. For caps of 10 and more the
     * threshold is what it was (10 → 3, 100 → 30, 1000 → 300).
     */
    function hintIsActionable(cap, remaining) {
        if (remaining >= cap) return false;
        return remaining <= Math.max(1, Math.floor(cap * 0.3));
    }

    /**
     * The counter's visual state — DERIVED, never configured twice.
     *
     * `level` drives both the upsell copy and the progress bar's colour, and it
     * comes from the SAME predicate that already decides whether the user is
     * near the wall. A second threshold constant here would be exactly the drift
     * this file exists to prevent:
     *
     *   ok     — plenty left           → accent
     *   warn   — hintIsActionable()    → warning colour (the CTA appears here too)
     *   spent  — remaining === 0       → danger colour (the hard note is up)
     *
     * `ratio` is REMAINING / cap: the bar DRAINS as the day is used up, matching
     * the number printed above it ("осталось 3 из 10"). A bar that filled up as
     * runs were spent would contradict its own label.
     *
     * Returns null for an unlimited / unconfigured cap — there is nothing to draw.
     */
    function usageState(slug) {
        var c = cap(slug);
        if (!c || c === Infinity) return null;
        var rem = Math.max(0, Math.min(c, API.remaining(slug)));
        return {
            cap: c,
            used: c - rem,
            remaining: rem,
            ratio: rem / c,
            level: rem === 0 ? 'spent' : (hintIsActionable(c, rem) ? 'warn' : 'ok')
        };
    }

    // Paint one mounted bar from the state above. The wrapper carries the level so
    // CSS can colour the fill (and anything else) without JS touching styles.
    function renderBar(root, st) {
        if (!root) return;
        var bar = root.querySelector('[data-tl-bar]');
        var fill = root.querySelector('[data-tl-bar-fill]');
        if (!bar || !fill) return;
        if (!st) { root.removeAttribute('data-tl-level'); hide(bar); return; }

        root.setAttribute('data-tl-level', st.level);
        fill.style.width = (st.ratio * 100).toFixed(2) + '%';
        bar.setAttribute('aria-valuemin', '0');
        bar.setAttribute('aria-valuemax', String(st.cap));
        bar.setAttribute('aria-valuenow', String(st.remaining));
        show(bar);
    }

    function markHit(el, on) {
        try { if (el && el.classList) el.classList[on ? 'add' : 'remove']('tl-hit'); } catch (_) {}
    }

    // The hard "limit reached" note wins over the soft "N left" hint — showing
    // both at once reads as a contradiction ("0 left" + "you've hit the limit").
    // PAID users get the counter too — abionix (Premium, 2026-07-10 support
    // chat) burned his whole cap without ever learning a limit existed; the
    // Otter lesson is "see the ceiling coming". They just never get the
    // upgrade CTA (nothing to upgrade to) — the hint's [data-tl-hint-cta]
    // anchor is hidden for them.
    /* ── ONE fill for every limit sentence (22.10.2026) ──────────────────────
       CLAUDE.md promises :remaining and :cap in limit texts (:plan and :paid the
       server fills). Until this the counter filled them, the «over» wall filled
       them on its own, and the MAIN wall text never did: /image-compressor,
       /hdr-photo, /dialogue-enhancer and /signature-check printed «:cap»
       literally on the wall in every locale. Every place that prints a limit
       sentence now goes through these two.
       The token ends where a name could not go on, so «:capacity» is not «:cap»;
       every occurrence is filled, not only the first; an unknown value leaves the
       token as it was rather than printing «undefined». */
    function fillLimitText(tpl, vals) {
        return String(tpl == null ? '' : tpl).replace(/:(remaining|cap)(?![A-Za-z0-9_])/g, function (whole, k) {
            var v = vals ? vals[k] : null;
            if (v == null || (typeof v === 'number' && isNaN(v))) return whole;
            return v === Infinity ? '∞' : String(v);
        });
    }
    function limitVals(slug) {
        return { cap: cap(slug), remaining: API.remaining(slug) };
    }
    /* The wall's body is server HTML ({!! !!}: a tool may put markup in it), so it
       is filled text node by text node, markup intact. Each text node keeps its own
       template: the next paint (a midnight rollover, a purchase in another tab)
       fills from the template again, not from yesterday's number. */
    function fillLimitNode(node, vals) {
        if (!node) return;
        try {
            var stack = [node];
            while (stack.length) {
                var n = stack.pop();
                if (n.nodeType === 3) {
                    if (n.__tlTpl == null) n.__tlTpl = n.nodeValue;
                    var t = fillLimitText(n.__tlTpl, vals);
                    if (t !== n.nodeValue) n.nodeValue = t;
                } else if (n.childNodes) {
                    for (var i = n.childNodes.length - 1; i >= 0; i--) stack.push(n.childNodes[i]);
                }
            }
        } catch (_) {}
    }

    function renderHint(slug) {
        var m = mounts[slug];
        if (!m) return;

        var c = cap(slug);
        var rem = API.remaining(slug);
        var paid = API.isPro();
        // ALWAYS-ON counter (Farid 2026-07-10): every capped tool shows
        // «Осталось N из M на сегодня» from the first visit — limits differ
        // per tool and nobody should discover one at the wall. Tools with no
        // configured cap (Infinity / fail-open) render nothing at all. The
        // UPSELL pieces — the checkout CTA and the «:plan: до :paid в день»
        // line (paid cap server-rendered from config) — appear only near
        // exhaustion, and never for paid users.
        var kill = m.noteVisible || !c || c <= 0 || c === Infinity || rem == null || rem === Infinity;
        // The paid line and its CTA are shown to every free user from the FIRST
        // visit (Farid 2026-07-31: «покажи сразу, пусть не скрывается»). Only the
        // bar's colour still tracks how close the wall is — see hintIsActionable.
        var showPaid = !paid;
        // The day is spent, or a press was just refused: the limit sentence is
        // the news, so it carries the weight — the counter and the wall's line
        // go bold (Фарид 22.10.2026, after the Premium window came down:
        // «не навязчиво просто сообщаем»). Nothing opens and nothing moves; the
        // words already on the page say it louder. Not for a paid plan, not for
        // an unconfirmed account (it spent nothing — its line names the way out).
        /* The day is spent, but what is LOADED is already counted today (or paid
           by this page): the next press will not bill. The tool says what a press
           would bill (API.pending), and the counter says the repeat is free instead
           of «0 из N» in bold — which reads as a wall. /video-merge, 22.10.2026: a
           guest came back with the same three clips to add transitions, saw
           «осталось 0 из 1», went to the Premium page and left. */
        var repeatFree = false;
        if (!paid && m.pendingKeys && rem != null && rem <= 0 && !isBlocked(slug)) {
            try { repeatFree = API.freshKeys(slug, m.pendingKeys).length === 0; } catch (_) {}
        }
        var hit = !paid && !isBlocked(slug) && !!c && c > 0 && c !== Infinity
            && rem != null && (rem <= 0 || m.noteVisible) && !repeatFree;
        /* The day is spent and nothing is loaded yet — but something WAS counted
           today, and that one is free to run again (begin() → isRepeat). The
           counter at «0 из 1» said only that the day was over. Bug report
           28.10.2026, /voice-enhancer: a guest got a broken result, came back
           twenty minutes later, read «0 из 1», wrote «дайте возможность ещё раз
           обработать» and left without picking the file — the same file would
           have run for nothing. Only where the day's list holds a unit: a press
           billed without a key (a run, not a file) leaves the list empty and
           re-running it is NOT free. */
        var againFree = false;
        if (hit && !m.noteVisible && !m.pendingKeys) {
            try { againFree = ((readState(slug).f) || []).length > 0; } catch (_) {}
        }
        for (var n = 0; n < m.notes.length; n++) markHit(m.notes[n], hit);

        // The bar lives on the WRAPPER, not inside the hint: when the wall is hit
        // the hint is replaced by the hard note, and the bar must stay — empty and
        // red — instead of vanishing at the exact moment it explains the most.
        var st = usageState(slug);
        for (var b = 0; b < m.roots.length; b++) renderBar(m.roots[b], st);

        for (var i = 0; i < m.hints.length; i++) {
            var h = m.hints[i];
            markHit(h.el, hit);
            if (kill || !h.text) { hide(h.el); continue; }
            var useRepeat = repeatFree && !!h.repeat;
            var line = fillLimitText(useRepeat ? h.repeat : (h.template || ':remaining of :cap free left today.'), { remaining: rem, cap: c });
            if (againFree && h.repeatAny) line += ' ' + h.repeatAny;
            h.text.textContent = line;
            if (useRepeat && !m.__repeatSaid) { m.__repeatSaid = 1; ae(slug, { f: 'limit_repeat_hint' }); }
            if (againFree && h.repeatAny && !m.__repeatAnySaid) { m.__repeatAnySaid = 1; ae(slug, { f: 'limit_repeat_any_hint' }); }
            var cta = h.el.querySelector('[data-tl-hint-cta]');
            if (cta && !cta.__tlSrcWired) {
                cta.__tlSrcWired = true;
                cta.addEventListener('click', function () {
                    try { window.__proSrc && window.__proSrc.set(slug + '-limit-hint'); } catch (_) {}
                    // Second half of the exposure pair: a click here is the only
                    // proof the block did its job. Latched on the mount so the
                    // exit event reports it whichever CTA was pressed.
                    var mm = mounts[slug];
                    if (mm) mm.__ctaClicked = 1;
                    ae(slug, { f: 'limit_block_cta', src: 'hint' });
                });
            }
            // One CTA per block. When the felt restrictions lead (promoteExtras)
            // they carry the pitch, and a second identical link under the
            // counter would just be noise pointing at the same page.
            var extrasLead = false;
            try {
                var pr = h.el.parentNode;
                var xb = pr && pr.querySelector('[data-tl-extras]');
                var xc = pr && pr.querySelector('[data-tl-extras-cta]');
                extrasLead = !!(xb && xb.children.length && xc);
            } catch (_) {}
            if (cta) cta.hidden = !showPaid || extrasLead;
            var pl = h.el.querySelector('[data-tl-paid]');
            if (pl) pl.hidden = !showPaid;
            show(h.el);
        }
    }

    /* ── The plan line ──────────────────────────────────────────────────────
       "Ваш тариф — Бесплатно" / "— Премиум", written from the tier snapshot.

       SIGNED-OUT VISITORS GET NOTHING HERE (Фарид, 2026-07-31): an anonymous
       person has no plan, and printing a role for them ("Гость", "Аноним")
       states an account status they do not have. They still see every number
       below — the ceiling without a label that misleads.

       Rendered client-side for the same reason as everything else in this file:
       the HTML is PageCached across visitors, so a server-rendered plan name
       would leak one visitor's tier to the next. */
    function renderPlan(slug) {
        var m = mounts[slug];
        if (!m || !m.roots) return;
        var t = window.__tierLimits;
        // No snapshot yet, or a signed-out visitor → stay silent. tier_key is the
        // only signal that distinguishes "guest" from "free account": is_pro is
        // false for both.
        var anon = !t || !t.tier_key || t.tier_key === 'anonymous';

        for (var i = 0; i < m.roots.length; i++) {
            var root = m.roots[i];
            var el = root.querySelector('[data-tl-plan]');
            if (!el) continue;
            // Strings ride on the root as data attributes — the same way
            // data-tl-template already does — so they are translated once by
            // Blade and this file never carries a dictionary.
            var name = anon ? '' : (API.isPro()
                ? (root.getAttribute('data-tl-plan-paid') || '')
                : (root.getAttribute('data-tl-plan-free') || ''));
            if (!name) { el.hidden = true; el.textContent = ''; continue; }
            el.textContent = '';
            el.appendChild(document.createTextNode((root.getAttribute('data-tl-plan-prefix') || '') + ' '));
            var b = document.createElement('span');
            b.className = 'tl-plan-name';
            b.textContent = name;
            el.appendChild(b);
            el.hidden = false;
        }
    }

    /* ── Every OTHER tier-limited parameter, in ONE place ───────────────────
       Modules that own a ceiling (quality-tier.js for export quality, and this
       file for the per-tool caps in the snapshot) push a line here instead of
       drawing their own banner next to their own control.

       `key` dedupes: re-gating on tier-bootstrap:ready, a repopulated <select>
       or a MutationObserver re-run must UPDATE the line, never add a second one.
       That duplication is precisely what this container exists to end. */
    function setDetail(slug, key, text) {
        var m = mounts[slug];
        if (!m || !m.roots) return;
        for (var i = 0; i < m.roots.length; i++) {
            var box = m.roots[i].querySelector('[data-tl-extras]');
            if (!box) continue;
            var row = box.querySelector('[data-tl-extra="' + key + '"]');
            if (!text) {                       // empty text = retract the line
                if (row) row.parentNode.removeChild(row);
            } else {
                if (!row) {
                    row = document.createElement('div');
                    row.className = 'tl-extra';
                    row.setAttribute('data-tl-extra', key);
                    box.appendChild(row);
                }
                row.textContent = text;
            }
            box.hidden = box.children.length === 0;
            promoteExtras(slug, m.roots[i], box);
        }
    }

    /* ⭐ Put the FELT restriction first.
     *
     * MEASURED 2026-08-09 (3 days, RU shard, both domains): the hard cap wall
     * was shown to 29 sessions out of 124 072 — 0.02% — while 6 497 sessions
     * saw this block. The counter it led with («осталось N из M на сегодня»)
     * therefore describes a budget almost nobody reaches, and the lines that
     * describe what the user actually gets — a mark on the export, a 720p
     * ceiling, a duration limit — were appended below it in small type.
     *
     * So whenever this box has anything in it, it moves ABOVE the counter and
     * takes the CTA; the counter keeps its place in the reading order right
     * after. When the box empties (a Pro snapshot lands and retracts every
     * line) everything returns exactly where the Blade put it — hence the
     * remembered anchor rather than a one-way move.
     *
     * NOT a redesign and NOT a new surface: same block, same CTA target, same
     * words, different order. The two CTAs report separately (src: 'hint' vs
     * 'extras') so the change is measurable against the 0.8% click rate it was
     * built from.
     */
    function promoteExtras(slug, root, box) {
        if (!root || !box) return;
        var hint = root.querySelector('[data-tl-hint]');
        var cta = root.querySelector('[data-tl-extras-cta]');
        var filled = box.children.length > 0;

        // Remember where Blade put it, once, so demotion is exact.
        if (box.__tlHome === undefined) box.__tlHome = box.nextSibling;

        if (filled && hint) {
            if (box.nextSibling !== hint) {
                try { hint.parentNode.insertBefore(box, hint); } catch (_) {}
                if (cta) { try { hint.parentNode.insertBefore(cta, hint); } catch (_) {} }
            }
        } else if (!filled && box.__tlHome !== undefined) {
            try { if (box.__tlHome) box.parentNode.insertBefore(box, box.__tlHome); } catch (_) {}
        }

        // The counter hides its own CTA while these lines lead, but renderHint
        // runs on the tier snapshot and setDetail runs later, when quality-tier
        // reports its ceilings — so the first pass decided "no lead" and left a
        // second, identical link under the counter. Re-render the counter
        // whenever the lead state actually flips. Guarded on the flag so this
        // cannot loop: renderHint never writes back into the extras box.
        if (root.__tlLead !== filled) {
            root.__tlLead = filled;
            try { renderHint(slug); } catch (_) {}
        }

        if (cta) {
            // Only a free user is being sold anything; a paid visitor keeps the
            // facts and loses the pitch, same rule the counter's CTA follows.
            cta.hidden = !(filled && !API.isPro());
            if (!cta.__tlSrcWired) {
                cta.__tlSrcWired = true;
                cta.addEventListener('click', function () {
                    try { window.__proSrc && window.__proSrc.set(slug + '-limit-extras'); } catch (_) {}
                    var mm = mounts[slug];
                    if (mm) mm.__ctaClicked = 1;
                    ae(slug, { f: 'limit_block_cta', src: 'extras' });
                });
            }
        }
    }

    /* Per-tool ceilings that live in the tier snapshot rather than in a gated
       control — duration and speed range today. Quality ceilings arrive through
       the same setDetail() from quality-tier.js, which is the module that knows
       which kinds a given page actually gates. */
    function renderSnapshotDetails(slug) {
        var m = mounts[slug];
        var t = window.__tierLimits;
        if (!m || !m.roots || !m.roots.length || !t) return;
        var root = m.roots[0];
        var str = function (k) { return root.getAttribute('data-tl-detail-' + k) || ''; };

        /* Watermark. Its own tier flag, not is_pro: `watermark` is resolved
           server-side per tier and is the only thing that actually decides
           whether the mark is drawn, so reading anything else here could promise
           a clean export to someone who will get a marked one. The attribute is
           present only where the mark is in scope at all.
           Disclosed BEFORE the export, which is the whole point — a user must not
           discover the mark in the finished file (Фарид: «должны знать заранее»). */
        setDetail(slug, 'watermark', (t.watermark && str('watermark')) ? str('watermark') : '');

        // Paid users are not being sold anything — the block states their plan
        // and their counter, and stops there.
        if (API.isPro()) { setDetail(slug, 'duration', ''); setDetail(slug, 'rate', ''); return; }

        /* Pick the unit the number survives. `Math.round(dur/60)` renders every
           ceiling under 30 s as "up to 0 min" — measured on /video-bg-remover,
           whose guest ceiling is 10 s: the line said the tool allowed nothing.
           Minutes stay untouched above the minute so no existing tool's wording
           moves. */
        var dur = t.tool_max_duration && t.tool_max_duration[slug];
        var durLine = '';
        if (dur && dur > 0) {
            durLine = (dur < 60 && str('duration-sec'))
                ? str('duration-sec').replace(':n', dur)
                : (str('duration') ? str('duration').replace(':n', Math.round(dur / 60)) : '');
        }
        setDetail(slug, 'duration', durLine);

        var rate = t.tool_rate && t.tool_rate[slug];
        var rmax = rate && (typeof rate === 'object' ? rate.max : rate);
        setDetail(slug, 'rate', (rmax && str('rate'))
            ? str('rate').replace(':n', rmax)
            : '');
    }

    /* ── Tier ceiling on INPUT DURATION ────────────────────────────────────────
       A second axis next to daily_runs: for speech/video tools the binding
       constraint is LENGTH, and megabytes say nothing about it (a 64 kbps
       audiobook is 9 hours in 246 MB; a WAV of the same size is 24 minutes).
       Source: config/tiers.php → tools.<slug>.max_duration_s → User::tierLimits
       → window.__tierLimits.tool_max_duration. 0/absent = no ceiling (fail-open).

       Until 2026-08-08 the ONLY enforcement of this number lived inside
       profanity-censor-core.js; this module merely PRINTED «до N мин». Anything
       else that wanted the wall had to copy the logic, so it stayed at three
       tools. The gate below is that logic, once. */
    function durationCap(slug) {
        try {
            var t = tiers();   // тот же снимок, что у cap(): гость судится и после 3 с ожидания
            if (!t || !t.tool_max_duration) return 0;
            return (t.tool_max_duration[slug] | 0) || 0;
        } catch (_) { return 0; }
    }

    // What the PAID plan would allow for this tool — rendered into the block by
    // <x-tool-limit> straight from config, so the dialog can state the real
    // number instead of a hardcoded promise. 0 = not disclosed here.
    function premiumDurationCap(slug) {
        var m = mounts[slug];
        var root = m && m.roots && m.roots[0];
        return root ? (parseInt(root.getAttribute('data-tl-dur-premium'), 10) || 0) : 0;
    }

    /* window.__tierLimits arrives only with the async /api/session-bootstrap.
       Judging a file before it lands would wave every file through — the exact
       hole a contract test caught in profanity-censor. Bounded wait, then
       fail-open (better to let a file run than to invent a paid wall).
       Prod dispatches on document, tests on window — listen to both. */
    function tierSnapshotReady(timeoutMs) {
        if (window.__tierLimits) return Promise.resolve();
        return new Promise(function (resolve) {
            var done = false;
            var fin = function () { if (!done) { done = true; resolve(); } };
            try { document.addEventListener('tier-bootstrap:ready', fin, { once: true }); } catch (_) {}
            try { window.addEventListener('tier-bootstrap:ready', fin, { once: true }); } catch (_) {}
            setTimeout(fin, timeoutMs || 3000);
        });
    }

    function ae(slug, data) {
        try {
            data.slug = slug;
            window.__ae && window.__ae('feature_use', data);
        } catch (_) {}
    }

    // "1 ч 20 мин" / "35 мин" — coarse on purpose: this is a ceiling, and second
    // precision would invite arguing with the number rather than reading it.
    // Units come from the mounted block (9 locales), never hardcoded here — an
    // Arabic or Korean page must not read a Russian «мин».
    // A length under a minute is said in seconds: rounded up to «1 мин», the
    // 10-s guest ceiling of /video-bg-remover promised six times what it allows.
    // `exact` — the FILE's own length (and a part's), printed beside a ceiling:
    // rounded to minutes, a 2:20 clip against the 2-min ceiling of /video-to-vr
    // read «Здесь 2 мин, а лимит — 2 мин» — a wall that contradicts itself
    // (2026-10-09). A file's length is a fact the person can check in any
    // player, so it keeps every unit that is not zero: «2 мин 20 с», «1 ч 20 с».
    function fmtDur(s, str, exact) {
        s = Math.round(s || 0);
        var uh = str('unit-h', 'h'), um = str('unit-min', 'min'), us = str('unit-sec', 's');
        if (s > 0 && s < 60) return s + ' ' + us;
        var h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
        if (exact) {
            var em = Math.floor((s % 3600) / 60), es = s % 60, out = [];
            if (h) out.push(h + ' ' + uh);
            if (em) out.push(em + ' ' + um);
            if (es) out.push(es + ' ' + us);
            if (out.length) return out.join(' ');
        }
        if (h > 0) return m > 0 ? (h + ' ' + uh + ' ' + m + ' ' + um) : (h + ' ' + uh);
        return Math.max(1, m) + ' ' + um;
    }

    /* The wall itself. Built here rather than in each blade so a newly capped
       tool writes zero markup and zero CSS — the copy comes from <x-tool-limit>
       (tool → family → tools.common chain, so a new tool needs no translation
       work either) and the colours from the global theme tokens. */
    /* ── Phone × long file: the honest sentence before a wait that usually ends
       blank (measured 2026-09-10, RU shard, 30 days, browser runs on the
       audio-cleanup tools, done / started by OS and input length):
           iOS      <5 min 697/780 (89%)   5–15 26/33 (79%)   15–30 1/8    30–60 2/10   60+ 2/10
           Android  <5 min 876/1065 (82%)  5–15 71/94 (76%)   15–30 21/27  30–60 9/24   60+ 3/12
           Windows  15–30 109/118 (92%)    30–60 46/48 (96%)  60+ 44/49 (90%)
       Above the cliff a phone run is a coin toss at best: the page is frozen the
       moment the app is backgrounded and unloaded when memory runs out, and no
       sensor survives to say so (support #7773: four runs, three tools, one
       35-minute Voice Memo, an hour of trying). This is NOT a wall — a tier that
       bought the length may continue — it is the choice offered BEFORE the wait,
       once per file. Thresholds are the measured cliffs, not tier numbers.
       Copy travels as data-tl-phone-* on the shared limit block (10 locales in
       tools.common), with English fallbacks for a page without the block. */
    var PHONE_ADVISORY_S = { ios: 15 * 60, android: 30 * 60 };
    var phoneAdvised = {};
    function phoneOs() {
        var ua = navigator.userAgent || '';
        if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
        if (/Android/i.test(ua)) return 'android';
        return '';
    }
    function phoneAdvisory(slug, durS, opts) {
        opts = opts || {};
        var os = phoneOs();
        var cliff = os ? PHONE_ADVISORY_S[os] : 0;
        // opts.serverBound — the caller's file goes to the server regardless: no advisory.
        if (!cliff || !(durS > cliff) || opts.serverBound) return Promise.resolve(true);
        var key = slug + '|' + Math.round(durS);
        if (phoneAdvised[key]) return Promise.resolve(true);
        phoneAdvised[key] = true;
        var m = mounts[slug];
        var root = m && m.roots && m.roots[0];
        var str = function (k, fallback) {
            var v = root && root.getAttribute('data-tl-' + k);
            return v || fallback;
        };
        var vals = { d: fmtDur(durS, function (k, fb) { return str('dur-' + k, fb); }), c: fmtDur(cliff, function (k, fb) { return str('dur-' + k, fb); }) };
        var body = str('phone-body', 'This one is :d. On a phone, recordings over :c rarely finish: the app is paused the moment you switch away, and memory runs out. It is safer to open this tool on a computer — or continue and keep the page open until the end.')
            .replace(/:(d|c)\b/g, function (whole, k) { return vals[k] !== undefined ? vals[k] : whole; });
        ae(slug, { feature: 'phone_duration_advisory', dur_s: Math.round(durS), os: os, cliff_s: cliff, server: opts.serverLane ? (opts.serverLane.premium ? 'premium' : 'upsell') : 'none' });
        /* The way OUT of the coin toss, when the tool has one: a server lane that
           does the work for the phone (config/server-lane.php + the tool passing
           opts.serverLane). Premium runs it; everyone else sees the same button
           with the plan's name on it and lands on pricing — the door is visible
           to the people it is sold to (Farid 2026-09-11: «пусть видят и хотят
           купить»). The tool starts the run itself when we resolve 'server'. */
        var sl = opts.serverLane || null;
        var buttons = [];
        if (sl) {
            buttons.push({ id: 'server', primary: true, label: sl.premium
                ? str('phone-server', 'Process on the server')
                : str('phone-server-premium', 'On the server (:plan)').replace(':plan', (root && root.getAttribute('data-tl-plan-paid')) || 'Premium') });
        }
        var file = opts.file || null;
        // On a phone the parts are cut to the measured cliff: each one short
        // enough to finish before the app is paused or unloaded.
        var parts = canCutInPlace(file) ? partsPlan(durS, cliff) : null;
        if (parts) loadParts();
        buttons.push({ id: 'continue', label: str('phone-continue', 'Continue on the phone'), primary: !sl });
        buttons.push({ id: 'split', label: parts ? pm('btn_parts', 'Process in parts (:n)').replace(':n', parts.n) : str('dur-split', 'Split into parts') });
        buttons.push({ id: 'cancel', label: str('dur-cancel', pm('cancel', 'Choose another file')) });
        return modalChoice(slug, '📱 ' + str('phone-title', 'A long recording on a phone'), body, buttons, 'data-tl-phone-dialog').then(function (choice) {
            ae(slug, { feature: 'phone_duration_advisory', src: 'choice', action: choice, os: os, parts: parts ? parts.n : 0 });
            if (choice === 'split') {
                if (parts) {
                    markGateSpoke(slug, 'parts');
                    startParts({ slug: slug, file: file, durS: durS, targetS: cliff, bound: 'device', plan: parts });
                    return false;
                }
                location.href = opts.splitUrl || ('/' + (window.__LOCALE || 'en') + '/chapter-splitter');
                return false;
            }
            if (choice === 'cancel') markGateSpoke(slug, 'cancel');
            if (choice === 'server') {
                if (sl && sl.premium) return 'server';
                ae(slug, { feature: 'server_lane_upsell', src: 'phone_advisory' });
                location.href = window.__PRO_URL || ('/' + (window.__LOCALE || 'en') + '/pricing');
                return false;
            }
            return choice === 'continue';
        });
    }

    /* One modal for every pre-flight choice this module draws (tier wall, phone
       advisory): same card, same keyboard rules (Escape / click outside =
       cancel), first button focused. Returns the chosen id. */
    function modalChoice(slug, title, body, buttons, marker) {
        return new Promise(function (resolve) {
            var wrap = document.createElement('div');
            wrap.setAttribute(marker || 'data-tl-dur-dialog', slug);
            // Явные longhands вместо `inset:0`: на Chrome ≤86 / Safari ≤14.0 /
            // Firefox ≤65 сокращение не поддерживается, фиксированная подложка
            // схлопывается за пределы экрана и кнопки диалога становятся
            // недостижимы. Коробка та же, поддержка универсальная.
            wrap.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:10000;display:flex;align-items:center;'
                + 'justify-content:center;padding:16px;background:rgba(0,0,0,.6)';
            var card = document.createElement('div');
            card.setAttribute('role', 'dialog');
            card.setAttribute('aria-modal', 'true');
            card.style.cssText = 'max-width:460px;width:100%;background:var(--bg-secondary,#161a21);'
                + 'color:var(--text-primary,#fff);border:1px solid var(--border,rgba(255,255,255,.12));'
                + 'border-radius:var(--radius-lg,16px);padding:22px;box-shadow:0 18px 60px rgba(0,0,0,.45)';
            var h = document.createElement('div');
            h.style.cssText = 'font-size:17px;font-weight:600;margin-bottom:8px';
            h.textContent = title;
            var p = document.createElement('div');
            p.style.cssText = 'font-size:14px;line-height:1.5;color:var(--text-secondary,#b9c0cc)';
            p.textContent = body;
            var row = document.createElement('div');
            row.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-top:18px';
            var mk = function (id, label, primary) {
                var b = document.createElement('button');
                b.type = 'button';
                b.textContent = label;
                b.style.cssText = 'flex:1 1 auto;min-height:40px;padding:9px 14px;font-size:14px;cursor:pointer;'
                    + 'border-radius:var(--radius-md,10px);border:1px solid var(--border,rgba(255,255,255,.14));'
                    + (primary
                        ? 'background:var(--accent,#3b82f6);color:var(--on-accent,#fff);border-color:transparent;font-weight:600'
                        : 'background:transparent;color:var(--text-primary,#fff)');
                b.addEventListener('click', function () { close(id); });
                return b;
            };
            for (var i = 0; i < buttons.length; i++) row.appendChild(mk(buttons[i].id, buttons[i].label, !!buttons[i].primary));
            card.appendChild(h); card.appendChild(p); card.appendChild(row);
            wrap.appendChild(card);
            var onKey = function (e) { if (e.key === 'Escape') close('cancel'); };
            var settled = false;
            function close(id) {
                if (settled) return;
                settled = true;
                document.removeEventListener('keydown', onKey);
                if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
                resolve(id);
            }
            wrap.addEventListener('click', function (e) { if (e.target === wrap) close('cancel'); });
            document.addEventListener('keydown', onKey);
            document.body.appendChild(wrap);
            var first = card.querySelector('button');
            if (first) try { first.focus(); } catch (_) {}
        });
    }

    /* ── «Split into parts» happens HERE, not on another page ────────────────
       Until 2026-09-23 every duration ceiling answered «Split into parts» with
       location.href = '/chapter-splitter' — a handoff that carried neither the
       file nor the length the parts had to fit, into a tool with the same
       whole-decode ceiling and a daily cap of its own (456 presses in 60 days
       on RU; 60 ZIPs; comment #1731). The page already holds the file, so the
       parts are cut here and handed back to the same tool one by one — see
       public/js/audio-parts.js for the measurements and the billing rule.

       THE DIALOG NEVER WAITS FOR THE NETWORK. The plan (how many parts, how
       long) is the gate's own arithmetic, so the dialog shows at once; the
       cutting module is fetched meanwhile, while the person reads. First version
       waited for the module before drawing the dialog, with a 4 s ceiling so a
       hung request could not freeze the gate — and on a loaded machine the
       ceiling fired first and the parts option silently disappeared (measured on
       the worktree while four suites ran; a slow phone connection is the same). */
    var PARTS_MARGIN = 0.97;          // a part stays under 97 % of the ceiling
    var PARTS_MIN_S = 20;             // never plan slivers
    var PARTS_START_CEILING_MS = 20000;
    /* Equal parts under the ceiling: split at ceiling-length points and the last
       part is a 40-second tail, a worse thing to hand anyone than three thirds.
       The last range runs to the end (to: null) — a header that under-reports
       the length must not cut the recording short. */
    function partsPlan(durS, targetS) {
        durS = +durS; targetS = +targetS;
        if (!(durS > 0) || !(targetS > 0)) return null;
        var capS = Math.max(PARTS_MIN_S, targetS * PARTS_MARGIN);
        var n = Math.max(2, Math.ceil(durS / capS));
        var len = durS / n, ranges = [];
        for (var i = 0; i < n; i++) ranges.push({ i: i, from: i * len, to: i === n - 1 ? null : (i + 1) * len });
        return { n: n, len: len, durS: durS, ranges: ranges };
    }
    /* Can this page take the parts back? Only through the uploader zone that
       delivered the file — never a guess (see zoneFor). */
    function canCutInPlace(file) {
        if (!file) return false;
        var z = zoneFor(file);
        return !!(z && typeof z.__uploaderInjectFile === 'function');
    }
    /* After «Process in parts» was pressed. The module usually arrived while the
       dialog was being read; if not, the person sees «Preparing part 1 of N…»
       instead of nothing, and a request that hangs ends in an honest error at
       20 s rather than an eternal wait. */
    function startParts(o) {
        var fired = false;
        var hint = setTimeout(function () {
            if (window.AudioParts || fired) return;
            try { window.__toast((pm('preparing', 'Preparing part :i of :n…')).replace(':i', '1').replace(':n', String(o.plan.n)), 'info'); } catch (_) {}
        }, 400);
        var give = new Promise(function (res) { setTimeout(function () { res(null); }, PARTS_START_CEILING_MS); });
        Promise.race([loadParts(), give]).then(function (AP) {
            fired = true;
            clearTimeout(hint);
            if (AP && typeof AP.start === 'function') {
                // The person may have picked another file while the module was
                // on its way: that choice wins, and it is counted, not silent.
                if (!canCutInPlace(o.file)) {
                    ae(o.slug, { f: 'parts_superseded' });
                    try { window.__gateSpoke = null; } catch (_) {}
                    return;
                }
                AP.start(o);
                return;
            }
            ae(o.slug, { f: 'parts_module_failed' });
            try { window.__gateSpoke = null; } catch (_) {}
            try { window.__toast(pm('cut_failed', 'Could not prepare part :i. Choose the file again.').replace(':i', '1'), 'error'); } catch (_) {}
        });
    }
    /* A helper module this primitive pulls in only when it is needed (the parts
       cutter): one script tag, versioned like every other
       lazy script (window.__jsSrc), resolved with the global it defines — or
       null, and a later call may try again. */
    var lazyScripts = Object.create(null);
    function loadOnce(path, global) {
        if (window[global]) return Promise.resolve(window[global]);
        if (!lazyScripts[path]) {
            lazyScripts[path] = new Promise(function (res) {
                try {
                    var el = document.createElement('script');
                    el.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc(path) : path;
                    el.async = true;
                    el.onload = function () { if (!window[global]) lazyScripts[path] = null; res(window[global] || null); };
                    el.onerror = function () { lazyScripts[path] = null; res(null); };
                    document.head.appendChild(el);
                } catch (_) { lazyScripts[path] = null; res(null); }
            });
        }
        return lazyScripts[path];
    }
    function loadParts() { return loadOnce('/js/audio-parts.js', 'AudioParts'); }
    /* What the person just read is the TRUTH about this file («longer than this
       browser can hold», «longer than your plan's limit»). The tool's own error
       path runs right after we resolve — and in every tool it says «wrong
       format» / «could not decode» (no tool reads err.userInformed), i.e. it
       contradicts the dialog it follows. timbrica-core.js's __toast stays
       silent for error toasts while this mark is fresh; audio-parts.js clears
       it the moment a part is handed over, so that file's own errors speak. */
    function markGateSpoke(slug, why) {
        try { window.__gateSpoke = { at: Date.now(), slug: slug, why: why || '' }; } catch (_) {}
    }
    /* Strings for everything parts-related come from a layout island
       (window.__partsMsg, tools.common.parts.* in 10 locales), NOT from the
       <x-tool-limit> block: the device ceiling fires on tools that have no
       block at all (11 of the 44 blades that call AudioDecoderRobust.robust),
       and there the dialog used to fall back to English on a Russian page. */
    function pm(k, fallback) {
        var m = window.__partsMsg || {};
        return (typeof m[k] === 'string' && m[k]) ? m[k] : fallback;
    }

    function durationDialog(slug, durS, tierS, premS, sellable, opts, bound, parts) {
        var m = mounts[slug];
        var root = m && m.roots && m.roots[0];
        var str = function (k, fallback) {
            var v = root && root.getAttribute('data-tl-dur-' + k);
            if (v) return v;
            // Units and the cancel label also live in the parts island, so a
            // page without the limit block still speaks its own language.
            var alt = { 'unit-h': 'unit_h', 'unit-min': 'unit_min', 'unit-sec': 'unit_s', 'cancel': 'cancel' }[k];
            return alt ? pm(alt, fallback) : fallback;
        };
        var nParts = parts && parts.n > 0 ? parts.n : 0;
        return new Promise(function (resolve) {
            var wrap = document.createElement('div');
            wrap.setAttribute('data-tl-dur-dialog', slug);
            // Явные longhands вместо `inset:0`: на Chrome ≤86 / Safari ≤14.0 /
            // Firefox ≤65 сокращение не поддерживается, фиксированная подложка
            // схлопывается за пределы экрана и кнопки диалога становятся
            // недостижимы. Коробка та же, поддержка универсальная.
            wrap.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:10000;display:flex;align-items:center;'
                + 'justify-content:center;padding:16px;background:rgba(0,0,0,.6)';
            var card = document.createElement('div');
            card.setAttribute('role', 'dialog');
            card.setAttribute('aria-modal', 'true');
            card.style.cssText = 'max-width:460px;width:100%;background:var(--bg-secondary,#161a21);'
                + 'color:var(--text-primary,#fff);border:1px solid var(--border,rgba(255,255,255,.12));'
                + 'border-radius:var(--radius-lg,16px);padding:22px;box-shadow:0 18px 60px rgba(0,0,0,.45)';
            var h = document.createElement('div');
            h.style.cssText = 'font-size:17px;font-weight:600;margin-bottom:8px';
            // bound === 'device': the ceiling is this browser's memory, not the
            // plan — a different sentence (devlim_* in tools.common), no plan CTA.
            var deviceBound = bound === 'device';
            h.textContent = deviceBound
                ? (nParts
                    ? pm('dev_title', 'This recording is longer than this browser can hold at once')
                    : '📱 ' + str('dev-title', 'This recording is longer than this device can process'))
                : '⏱️ ' + str('title', 'This file is longer than your limit');
            var p = document.createElement('div');
            p.style.cssText = 'font-size:14px;line-height:1.5;color:var(--text-secondary,#b9c0cc)';
            // The paid number is stated ONLY when the upgrade would actually take
            // THIS file. Otherwise the honest sentence is the plain one — the way
            // out is splitting, and dangling a plan here would sell a known failure.
            /* One pass, longest placeholder first.
               The old chain was .replace(':p').replace(':d').replace(':c'), and
               ':p' is a prefix of ':plan' — which this sentence names. So the
               FIRST ':p' found sat inside ':plan', and every locale rendered
               "В 1 чlan — до :p": the plan name destroyed, the paid ceiling left
               as a raw placeholder, on the one dialog whose whole job is to sell
               Premium. ':plan' was never substituted at all; it comes from the
               same block the plan line already uses. */
            var durVals = {
                plan: (root && root.getAttribute('data-tl-plan-paid')) || '',
                d: fmtDur(durS, str, true),
                c: fmtDur(tierS, str),
                p: premS > 0 ? fmtDur(premS, str) : '',
                n: nParts,
                l: nParts ? fmtDur(parts.len, str, true) : '',
                r: ''
            };
            var capped = (function () { var c = cap(slug); return c !== Infinity && c > 0; })();
            var remToday = capped ? API.remaining(slug) : Infinity;
            if (remToday !== Infinity) durVals.r = String(remToday);
            var bodyText;
            if (deviceBound && nParts) {
                // Our limitation, our remedy, one unit for the whole recording.
                bodyText = pm('dev_body', 'It is :d; this browser can hold up to :c of audio at once. The tool can process it in parts right here: :n parts of about :l each, one after another.')
                    + (capped ? ' ' + pm('dev_body_capped', 'The daily limit counts the whole recording once.') : '');
            } else if (deviceBound) {
                bodyText = str('dev-body', 'It is :d. In this browser the tool can hold up to :c of audio at once; a longer file runs out of memory before it finishes. Split it into parts, or open the tool on a computer.');
            } else {
                bodyText = sellable && premS > 0
                    ? str('body-prem', 'It is :d. Your limit is :c per file; :plan raises it to :p.')
                    : str('body', 'It is :d. Your limit is :c per file.');
                // The plan's per-file length is what it sells, so each part is a
                // file of its own — said BEFORE anyone starts, with today's count.
                if (nParts) {
                    bodyText += ' ' + (capped && remToday !== Infinity
                        ? pm('tier_parts_line', 'It can also be split into :n parts; each part counts as a separate file in your daily limit (:r left today).')
                        : pm('tier_parts_line_uncapped', 'It can also be split into :n parts, processed one after another.'));
                    // More parts than today's runs: say so BEFORE the first
                    // part, not at the wall after the second (comment #1731).
                    if (capped && remToday !== Infinity && remToday > 0 && remToday < nParts) {
                        bodyText += ' ' + pm('tier_parts_rest', 'Today covers :r of them; the rest tomorrow.');
                    }
                }
            }
            p.textContent = bodyText.replace(/:(plan|d|c|p|n|l|r)\b/g, function (whole, key) {
                return durVals[key] !== undefined ? durVals[key] : whole;
            });
            var row = document.createElement('div');
            row.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-top:18px';
            var mk = function (id, label, primary) {
                var b = document.createElement('button');
                b.type = 'button';
                b.textContent = label;
                b.style.cssText = 'flex:1 1 auto;min-height:40px;padding:9px 14px;font-size:14px;cursor:pointer;'
                    + 'border-radius:var(--radius-md,10px);border:1px solid var(--border,rgba(255,255,255,.14));'
                    + (primary
                        ? 'background:var(--accent,#3b82f6);color:var(--on-accent,#fff);border-color:transparent;font-weight:600'
                        : 'background:transparent;color:var(--text-primary,#fff)');
                b.addEventListener('click', function () { close(id); });
                return b;
            };
            if (sellable) row.appendChild(mk('pro', str('cta', 'See Premium'), true));
            var splitLabel = !nParts ? str('split', 'Split into parts')
                : (deviceBound ? pm('btn_parts', 'Process in parts (:n)') : pm('btn_split', 'Split into parts (:n)')).replace(':n', nParts);
            row.appendChild(mk('split', splitLabel, !sellable));
            row.appendChild(mk('cancel', str('cancel', 'Choose another file')));
            card.appendChild(h); card.appendChild(p); card.appendChild(row);
            wrap.appendChild(card);
            var onKey = function (e) { if (e.key === 'Escape') close('cancel'); };
            var settled = false;
            function close(id) {
                if (settled) return;
                settled = true;
                document.removeEventListener('keydown', onKey);
                if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
                resolve(id);
            }
            wrap.addEventListener('click', function (e) { if (e.target === wrap) close('cancel'); });
            document.addEventListener('keydown', onKey);
            document.body.appendChild(wrap);
            var first = card.querySelector('button');
            if (first) try { first.focus(); } catch (_) {}
        });
    }

    /* ── Was the offer ever actually SEEN? ────────────────────────────────────
       Every event this module emitted until 2026-08-08 was about hitting a wall:
       daily_limit_hit, limit_tier_blocked, limit_precheck_block, limit_free_rerun.
       Nothing said whether the block was ever in front of anyone — so the one
       number that decides what to do next was unmeasurable.

       It matters because of the shape of the funnel (30-day measurement): 392 355
       sessions finish a job, 992 of them ever reach the checkout. Whether that is
       "the offer is shown and ignored" or "the offer is below the fold and never
       shown" changes the fix completely, and no data on the site could tell the
       two apart.

       Two events, both per page-load per slug, and only for people who could
       actually buy (a paid user is not being offered anything):
         limit_block_view      — first time the block is genuinely on screen.
                                 Sent immediately: reliable, survives a lost unload.
         limit_block_exposure  — on page exit: how long it was visible, whether the
                                 CTA was clicked, what the counter said at the end.
       `seen: 0` in the exposure event is the interesting case — the block existed
       and was never looked at. */
    var IO_RATIO = 0.5;      // half the block on screen counts as shown
    function watchExposure(slug) {
        var m = mounts[slug];
        if (!m || m.__expWired || !m.roots || !m.roots.length) return;
        var root = m.roots[0];
        if (!root || typeof window.IntersectionObserver !== 'function') return;
        m.__expWired = true;

        var seen = 0, seenMs = 0, since = 0, sentView = false, sentExp = false;
        var stop = function () {
            if (since) { seenMs += Date.now() - since; since = 0; }
        };
        /* The snapshot decides BOTH whether this person is being sold anything and
           what the counter says, and it arrives asynchronously. A block that is
           already on screen when it lands would never intersect again, so the view
           event is deferred rather than dropped — otherwise every above-the-fold
           block would silently report nothing. */
        var maybeView = function () {
            if (sentView || !window.__tierLimits || API.isPro()) return;
            var st = usageState(slug);
            if (!st) return;                       // no cap here → nothing was offered
            sentView = true;
            ae(slug, { f: 'limit_block_view', cap: st.cap, rem: st.remaining, note: m.noteVisible ? 1 : 0 });
        };
        try { document.addEventListener('tier-bootstrap:ready', function () { if (seen) maybeView(); }); } catch (_) {}

        var io = new window.IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
                var vis = entries[i].isIntersecting && entries[i].intersectionRatio >= IO_RATIO
                    && document.visibilityState !== 'hidden';
                if (vis && !since) {
                    since = Date.now();
                    seen = 1;
                    maybeView();
                } else if (!vis) { stop(); }
            }
        }, { threshold: [0, IO_RATIO, 1] });
        try { io.observe(root); } catch (_) { m.__expWired = false; return; }

        var flush = function () {
            if (sentExp || API.isPro()) return;
            var st = usageState(slug);
            // A tool with no cap for this viewer draws no block, so "it was never
            // seen" would be noise, not a finding. Only tools that actually had
            // something on screen report their exposure.
            if (!st) return;
            sentExp = true;
            stop();
            ae(slug, {
                f: 'limit_block_exposure',
                seen: seen,
                seen_ms: Math.round(seenMs),
                cta: m.__ctaClicked ? 1 : 0,
                cap: st.cap,
                rem: st.remaining,
                note: m.noteVisible ? 1 : 0
            });
            try { io.disconnect(); } catch (_) {}
        };
        /* ⚠️ NOT on pagehide alone — measured 2026-08-08 and it loses the event.
           telemetry-tier0.js registers its own pagehide handler when it loads,
           i.e. BEFORE this one, and that handler drains the queue. Anything this
           module enqueues afterwards sits in an already-flushed queue and dies
           with the page. So the report goes out on a TIMER, while the transport
           is still alive; the exit handlers stay as a second chance for the rare
           case where the timer has not fired yet.
           EXPOSURE_MS is also a judgement, not just plumbing: someone who has not
           scrolled to the block within 20 s was not going to see it. */
        // Overridable so the live test can prove the timer path in seconds rather
        // than sitting through the real window (a test that waits 20 s gets
        // skipped, and a skipped test proves nothing).
        var EXPOSURE_MS = (window.__tlExposureMs | 0) || 20000;
        // ⚠️ The countdown starts when the SNAPSHOT lands, not when the block
        // mounts. mount() runs at parse time, long before /api/session-bootstrap
        // answers; a timer started there fires while usageState() is still null,
        // flush() bails on the empty state, and — since nothing re-arms it — the
        // report is lost for good. Measured exactly that way before this guard.
        var armed = false;
        var arm = function () {
            if (armed || !window.__tierLimits) return;
            armed = true;
            setTimeout(flush, EXPOSURE_MS);
        };
        arm();
        try { document.addEventListener('tier-bootstrap:ready', arm); } catch (_) {}
        try { window.addEventListener('tier-bootstrap:ready', arm); } catch (_) {}
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'hidden') { stop(); flush(); }
        });
        window.addEventListener('pagehide', flush);
    }

    /* ── The counter and the Premium line UNDER THE UPLOAD ZONE (09.10.2026) ──
       Фарид: «все должны знать, что у Премиума такой лимит огромный» → «показывай
       сразу под зоной загрузки» → «не захардкодено, у каждого инструмента своя
       верная цифра». A guest sweep of 235 capped tools found the full block
       rendered before the first pick on 138; on 81 it lives in a panel that
       appears only after a file is chosen (the .workspace gate, nested tool
       panels), so the Premium number was learned at the press or at the wall.

       The line is a MIRROR of the tool's own block, built from it: the same
       counter sentence (data-tl-template), the same server-rendered paid line
       (limit_paid_line with THIS tool's :paid), the same CTA — and mounted on the
       same slug, so renderHint fills it with the live cap()/remaining() and the
       numbers can never disagree with the block. Nothing here knows a number.

       Shown while the full block is not rendered near the zone (not rendered at
       all, or starting more than ZONE_NEAR_PX below it); hidden for a paid plan
       (nothing to sell — the block itself still counts for them) and where the
       tool sells no Premium (no paid line in the block).

       ⚠️ "Not rendered" has two causes and only one of them wants a mirror. A block
       inside a panel that has not opened yet is the case above. A block the TOOL
       took off the screen — class `hidden` or the hidden attribute on its own root —
       is "nothing here can be run" (docs/why/nothing-to-run.md: a verdict stands
       where the launch offer stood, and the counter leaves with the offer). Reading
       that as "not rendered yet" put the counter straight back under the zone, next
       to the verdict: /repair-video since 09.10, found 10.10 when /repair-audio
       took the same rule. */
    var ZONE_NEAR_PX = 160;
    /** Pure decision, exposed for its test. */
    function zoneLineWanted(o) {
        if (!o || o.paid || o.withdrawn) return false;
        if (!o.fullRendered) return true;
        return (o.fullTop - o.zoneBottom) > ZONE_NEAR_PX;
    }
    function rendered(el) {
        try { return !!el && el.isConnected !== false && el.getClientRects().length > 0; } catch (_) { return false; }
    }
    /** The tool itself hid the block's root — not an ancestor, not a closed panel. */
    function withdrawnByTool(full) {
        try { return !!full && (full.hidden === true || !!(full.classList && full.classList.contains('hidden'))); } catch (_) { return false; }
    }
    function fullRootOf(m) {
        for (var i = 0; i < ((m && m.roots) || []).length; i++) {
            var r = m.roots[i];
            if (r && r.classList && r.classList.contains('tl-limit') && !r.__tlZoneLine) return r;
        }
        return null;
    }
    function syncZoneLine(slug) {
        var m = mounts[slug], z = m && m.zoneLine;
        if (!z) return;
        var full = m.zoneFull, zone = z.__tlZone, fr = rendered(full), want;
        try {
            want = zoneLineWanted({
                paid: API.isPro(),
                withdrawn: withdrawnByTool(full),
                fullRendered: fr,
                fullTop: fr ? full.getBoundingClientRect().top : 0,
                zoneBottom: rendered(zone) ? zone.getBoundingClientRect().bottom : 0
            });
        } catch (_) { want = false; }
        var was = z.style.display !== 'none';
        z.style.display = want ? '' : 'none';
        if (want && !was) watchZoneLine(slug);
    }
    function watchZoneLine(slug) {
        var m = mounts[slug], z = m && m.zoneLine;
        if (!z || z.__tlSeenWired || typeof window.IntersectionObserver !== 'function') return;
        z.__tlSeenWired = true;
        try {
            var io = new window.IntersectionObserver(function (entries) {
                for (var i = 0; i < entries.length; i++) {
                    if (entries[i].isIntersecting && entries[i].intersectionRatio >= IO_RATIO && !m.__zoneSeen && usageState(slug)) {
                        m.__zoneSeen = 1;
                        ae(slug, { f: 'limit_zone_line_seen' });
                        try { io.disconnect(); } catch (_) {}
                    }
                }
            }, { threshold: [0, IO_RATIO] });
            io.observe(z);
        } catch (_) {}
    }
    function mountZoneLines() {
        for (var slug in mounts) {
            var m = mounts[slug];
            if (!m || m.zoneLine !== undefined) continue;
            var full = fullRootOf(m);
            var paidEl = full && full.querySelector('[data-tl-paid]');
            var ctaEl = full && full.querySelector('[data-tl-hint-cta]');
            // The block's own Premium line: in its hint, or on its root (a block with
            // :hint="false" renders no hint but still carries the line for this mirror).
            var paidText = (paidEl && paidEl.textContent) || (full && full.getAttribute('data-tl-paid-line')) || '';
            var href = (ctaEl && (ctaEl.getAttribute('href') || ctaEl.href)) || (full && full.getAttribute('data-tl-cta-href')) || '';
            if (!full || !paidText || !href) { m.zoneLine = null; continue; }   // nothing to mirror, or no Premium sold here
            // The tool prints its own counter in this place (x-tool-limit :zone-line="false").
            if (full.hasAttribute('data-tl-no-zone-line')) { m.zoneLine = null; continue; }
            var zone = null, zones = document.querySelectorAll('.tool-uploader');
            for (var i = 0; i < zones.length; i++) { if (!zones[i].__tlZoneLine && rendered(zones[i])) { zone = zones[i]; break; } }
            // No upload zone on the first screen (a recorder, a generator): under the
            // tool's subtitle, right above its interface. Inside the tutor's row the
            // H2 shares a line with the «how to» door, so the row is the anchor.
            if (!zone) {
                var h2 = document.querySelector('.tool-h2');
                if (h2 && h2.parentNode && h2.parentNode.classList && h2.parentNode.classList.contains('tt-h2row')) h2 = h2.parentNode;
                if (h2 && !h2.__tlZoneLine && rendered(h2)) zone = h2;
            }
            if (!zone || !zone.parentNode) continue;                         // nothing on screen yet — asked again on the next pass
            var root = document.createElement('div');
            // pre-upload-visible: the zone may be a direct child of a .workspace
            // that is not .active yet, and the gate in app.css hides every other child.
            root.className = 'tl-limit-mirror tl-zone-line pre-upload-visible';
            root.setAttribute('data-tl-zone-line', slug);
            root.__tlZoneLine = true;
            root.__tlZone = zone;
            var hint = document.createElement('div');
            hint.className = 'tl-hint'; hint.setAttribute('data-tl-hint', ''); hint.hidden = true;
            var text = document.createElement('span');
            text.setAttribute('data-tl-hint-text', '');
            // The Premium number IS the link here: «Премиум: до 1000 … в день» next to
            // a «Премиум: лимит выше» button said the word twice in one short line.
            // Both attributes, so renderHint shows and hides it as one piece.
            var link = document.createElement('a');
            link.className = 'tl-cta tl-paid-link';
            link.setAttribute('data-tl-paid', ''); link.setAttribute('data-tl-hint-cta', '');
            link.hidden = true;
            link.href = href;
            link.textContent = paidText;
            // Its own source, so the checkout can tell this door from the block's.
            link.__tlSrcWired = true;
            (function (s, mm) {
                link.addEventListener('click', function () {
                    try { window.__proSrc && window.__proSrc.set(s + '-limit-zone'); } catch (_) {}
                    mm.__ctaClicked = 1;
                    ae(s, { f: 'limit_block_cta', src: 'zone' });
                });
            })(slug, m);
            hint.appendChild(text); hint.appendChild(link);
            root.appendChild(hint);
            zone.parentNode.insertBefore(root, zone.nextSibling);
            zone.__tlZoneLine = root;
            m.zoneLine = root;
            m.zoneFull = full;
            API.mount(slug, { root: root, hint: hint, hintText: text, template: full.getAttribute('data-tl-template') || '',
                repeat: full.getAttribute('data-tl-repeat') || '', repeatAny: full.getAttribute('data-tl-repeat-any') || '' });
            (function (s) {
                var again = function () { syncZoneLine(s); };
                try {
                    if (typeof window.ResizeObserver === 'function') {
                        var ro = new window.ResizeObserver(again);
                        ro.observe(full); ro.observe(zone);
                        if (document.body) ro.observe(document.body);
                    }
                } catch (_) {}
                try { window.addEventListener('resize', again); } catch (_) {}
                try { document.addEventListener('tier-bootstrap:ready', again); } catch (_) {}
            })(slug);
            syncZoneLine(slug);
        }
    }

    /* ── Experiment wall_price: the price «per day» at the wall ──────────────
       The component renders the line for every visitor, hidden (this HTML is
       PageCached, one copy for all), and experiment.js throws the arm on the
       client. Only `per_day` sees it, and only where the wall sells: never to a
       paid plan, never on the «confirm your e-mail» variant. The outcome — a
       purchase — happens on the checkout, days later, so a wall the person
       actually SAW is recorded (__exp.expose) and the stages go out in the
       shape experiments:report reads (config/experiments.php → wall_price). */
    var WALL_PRICE = 'wall_price';
    var wallPriceShown = Object.create(null);
    function wallPriceArm() {
        try { return (window.__exp && window.__exp.arm(WALL_PRICE)) || null; } catch (_) { return null; }
    }
    function wallPriceSeen(slug, blocked) {
        if (wallPriceShown[slug] || blocked || API.isPro()) return;
        var m = mounts[slug];
        if (!m || !m.notes.length || !m.notes[0].querySelector('[data-tl-price]')) return;
        try {
            if (!window.__exp || !window.__exp.expose || !window.__exp.expose(WALL_PRICE)) return;
            wallPriceShown[slug] = 1;
            window.__exp.stage(WALL_PRICE, 'shown', { slug: slug });
        } catch (_) {}
    }

    // A holder of ANY paid plan (legacy Pro or Premium — both is_pro, both on the
    // same raised cap) has nothing to upgrade to. Show them the plain "resets
    // tomorrow" line, hide the upsell copy and the checkout CTA. Decided here and
    // not in Blade because is_pro only arrives with the async session bootstrap,
    // while the HTML is PageCached and shared across visitors.
    function paintNote(note, blocked, over) {
        var paid = API.isPro();
        var free = note.querySelector('[data-tl-body-free]');
        var forPaid = note.querySelector('[data-tl-body-paid]');
        // Fourth variant: the queue is bigger than what is left, which is NOT
        // the same as having spent everything — a first-time visitor who drops
        // 40 files against a cap of 10 has used nothing at all, and the free
        // line ("you have used your daily limit") would simply be false.
        var forOver = note.querySelector('[data-tl-body-over]');
        // Third variant: an unverified account never "used up" anything, so the
        // "you've spent today's runs, come back tomorrow" copy would be a lie —
        // tomorrow is zero too. Its own line says what is actually true and names
        // the paid plan's daily number (Farid: «показывай им что лимит повышенный
        // у премиум аккаунтов»). Blades that predate it simply lack the node.
        var forBlocked = note.querySelector('[data-tl-body-blocked]');
        blocked = !!blocked && !paid && !!forBlocked;
        over = !!over && !paid && !blocked && !!forOver;
        var cta = note.querySelector('[data-tl-cta]');
        if (free) free.hidden = paid || blocked || over;
        if (forPaid) forPaid.hidden = !paid;
        if (forBlocked) forBlocked.hidden = !blocked;
        var price = note.querySelector('[data-tl-price]');
        if (price) price.hidden = paid || blocked || wallPriceArm() !== 'per_day';
        if (forOver) forOver.hidden = !over;
        // Every variant of the wall is filled — not only «over»: the free body of
        // four tools carried :cap and printed it verbatim (see fillLimitText).
        var vals = limitVals(note.__tlSlug || '');
        /* The day's NUMBER on the wall — the tool's own counter sentence, «0 из N»
           (comment #2018, /vocal-remover 25.10.2026: «а что значит Вы использовали
           дневной лимит…» from a guest who had spent the day's one song on another
           song: the counter is hidden under the wall, and the body never says how
           big the limit is). Only a spent day: the unconfirmed account and «выбрано
           больше, чем осталось» carry their own numbers, and «осталось 1 из 1» under
           a refusal would contradict it. The component renders the node only where
           the wall's own body names no :cap / :remaining. */
        var count = note.querySelector('[data-tl-count]');
        var counted = !!count && !blocked && !over && typeof vals.cap === 'number'
            && vals.cap > 0 && vals.cap !== Infinity && vals.remaining != null && vals.remaining <= 0;
        if (count) { count.hidden = !counted; fillLimitNode(count, vals); }
        try { note.classList[counted ? 'add' : 'remove']('tl-counted'); } catch (_) {}
        fillLimitNode(free, vals);
        fillLimitNode(forPaid, vals);
        fillLimitNode(forBlocked, vals);
        fillLimitNode(forOver, vals);
        if (cta) {
            cta.hidden = paid;
            // Purchase attribution: remember WHICH tool's limit wall sent the
            // user to checkout (read back by the pro funnel events).
            if (!cta.__tlSrcWired) {
                cta.__tlSrcWired = true;
                cta.addEventListener('click', function () {
                    var sl = 'tool';
                    try {
                        var mEl = note.closest && note.closest('[data-tool-limit]');
                        sl = (mEl && mEl.getAttribute('data-tool-limit')) || 'tool';
                        window.__proSrc && window.__proSrc.set(sl + '-limit');
                    } catch (_) {}
                    // Same latch as the hint's CTA — the wall's button counts too.
                    var mm = mounts[sl];
                    if (mm) mm.__ctaClicked = 1;
                    ae(sl, { f: 'limit_block_cta', src: 'wall' });
                    if (wallPriceShown[sl]) {
                        try { window.__exp.stage(WALL_PRICE, 'cta', { slug: sl }); } catch (_) {}
                    }
                });
            }
        }
    }

    /* The walled user's ACTION is the tool's big primary button — and it used to
       stay a vivid, inviting CTA that reacted to a refused press with ZERO pixel
       changes. Measured cost (session 32117768, 2026-07-21, /join): 21 presses in
       2 minutes, 2 rage_clicks, a hopeful page reload, CTA never clicked, left.
       So while the note is up: (a) the button itself answers — dimmed
       .tl-btn-blocked + aria-disabled (NOT disabled= : the click must still reach
       begin() so the state re-evaluates after midnight / a purchase in another
       tab / a repeats-only queue); (b) EVERY refused press restarts a pulse on
       the note (remove class → reflow → re-add), so press #2..#21 visibly answer;
       (c) the note is brought into view with block:'nearest' — the one scroll
       form the no-auto-scroll policy allows (only scrolls if off-screen). */
    function paintButtons(slug, blocked) {
        var m = mounts[slug];
        if (!m || !m.buttons) return;
        var list = m.buttons.concat(m.autoButtons || []);
        for (var i = 0; i < list.length; i++) {
            var b = list[i];
            try {
                if (blocked) { b.classList.add('tl-btn-blocked'); b.setAttribute('aria-disabled', 'true'); }
                else { b.classList.remove('tl-btn-blocked'); b.removeAttribute('aria-disabled'); }
            } catch (_) {}
        }
    }
    function pulseNote(slug) {
        var m = mounts[slug];
        if (!m) return;
        for (var i = 0; i < m.notes.length; i++) {
            var n = m.notes[i];
            try {
                n.classList.remove('tl-note-pulse');
                void n.offsetWidth; // reflow — restarts the animation on every refused press
                n.classList.add('tl-note-pulse');
            } catch (_) {}
        }
        try { if (m.notes[0] && m.notes[0].scrollIntoView) m.notes[0].scrollIntoView({ block: 'nearest' }); } catch (_) {}
    }
    // quiet: the note is being revealed BY US (an unverified account on page load),
    // not as the answer to a press — so no pulse and no scrolling. Both of those
    // exist to answer a refused click; firing them unprompted would move the page
    // under a user who has not touched anything yet.
    /* What the wall currently SAYS — the one variant paintNote left visible.
       innerText would read them all: when the note's container is not rendered
       (a hidden result panel) the browser falls back to textContent and the
       hidden siblings come along for the ride. */
    function noteLine(slug, blocked, over) {
        var m = mounts[slug];
        if (!m || !m.notes.length) return '';
        try {
            var sel = API.isPro() ? '[data-tl-body-paid]'
                : (blocked ? '[data-tl-body-blocked]'
                    : ((over && m.notes[0].querySelector('[data-tl-body-over]')) ? '[data-tl-body-over]' : '[data-tl-body-free]'));
            var line = m.notes[0].querySelector(sel);
            // The toast and the screen reader say what the wall says — its number first.
            var count = m.notes[0].querySelector('[data-tl-count]');
            var lead = count && !count.hidden ? String(count.textContent || '').trim() : '';
            var body = (line ? line.textContent : m.notes[0].textContent).trim();
            return lead && line ? lead + ' ' + body : body;
        } catch (_) { return ''; }
    }

    /* ── Did the refusal actually LAND on the screen? ─────────────────────────
       Until now begin() promised its callers «null ⇒ blocked (note shown for
       you)» and had no way of knowing whether that was true. It depends
       entirely on the tool's DOM — which pane the block sits in, whether that
       pane is displayed, whether the page can scroll to it — and when it is
       false the user gets a button that does nothing at all.

       MEASURED, /image-metadata, 2026-08-30: two bug reports from one person,
       442 refused presses across 40 sessions, and `limit_block_exposure` said
       seen=0 for every one of them — the block lives inside a tab pane that is
       display:none unless the «Очистка» tab is open, so show(), the pulse and
       the block:'nearest' scroll were all no-ops on a zero-height element.

       So the promise is now checked rather than assumed. Returns the reason the
       wall is not readable, or '' when it is. */
    function noteUnreachableReason(slug) {
        var m = mounts[slug];
        if (!m || !m.notes.length) return 'no-note';
        var worst = 'offscreen';
        for (var i = 0; i < m.notes.length; i++) {
            var n = m.notes[i];
            try {
                var r = n.getBoundingClientRect();
                if (!r.width || !r.height) { worst = 'no-box'; continue; }
                var hiddenChain = false;
                for (var p = n; p && p !== document.documentElement; p = p.parentElement) {
                    var cs = getComputedStyle(p);
                    if (cs.visibility === 'hidden' || cs.display === 'none') { hiddenChain = true; break; }
                }
                if (hiddenChain) { worst = 'invisible'; continue; }
                // Partially on screen counts: the pulse draws the eye to it.
                var vh = window.innerHeight || document.documentElement.clientHeight || 0;
                if (r.bottom > 4 && r.top < vh - 4) return '';
            } catch (_) {}
        }
        return worst;
    }

    /* The refusal did not land. Deliver it on the one channel a tool's layout
       cannot swallow — the site-wide toast — reusing the note's OWN translated
       sentence and its CTA, so this adds no copy in any locale. Actionable
       toasts stay until dismissed, so one standing answer is enough: a second
       press re-pulses it rather than stacking a duplicate. */
    function escalateUnreachable(slug, reason, blocked, over) {
        var msg = noteLine(slug, blocked, over);
        if (!msg || typeof window.__toast !== 'function') return;
        try {
            var live = document.querySelector('[data-tl-wall-toast="' + slug + '"]');
            if (live && !live.classList.contains('hide')) {
                live.classList.remove('show');
                void live.offsetWidth;
                live.classList.add('show');
                return;
            }
        } catch (_) {}
        var m = mounts[slug];
        var ctaEl = null, ctaHref = '';
        try {
            ctaEl = m && m.notes[0] && m.notes[0].querySelector('[data-tl-cta]');
            if (ctaEl && !ctaEl.hidden) ctaHref = ctaEl.getAttribute('href') || '';
        } catch (_) {}
        var opts = { silent: true };
        if (ctaHref) {
            opts.action = {
                label: (ctaEl.textContent || '').trim(),
                onclick: function () {
                    try {
                        window.__proSrc && window.__proSrc.set(slug + '-limit');
                        var mm = mounts[slug];
                        if (mm) mm.__ctaClicked = 1;
                    } catch (_) {}
                    ae(slug, { f: 'limit_block_cta', src: 'toast' });
                    window.location.href = ctaHref;
                },
            };
        }
        try { window.__toast(msg, 'warning', opts); } catch (_) { return; }
        // Tag it so the next refused press finds this toast instead of adding one.
        try {
            var c = document.getElementById('toastContainer');
            var last = c && c.lastElementChild;
            if (last) last.setAttribute('data-tl-wall-toast', slug);
        } catch (_) {}
        // A wall that cannot be seen is a DEFECT in the page, not a user event.
        // Reported so the class stays measurable instead of silent — that is
        // exactly how /image-metadata went unnoticed for a month while its own
        // exposure sensor was printing seen=0.
        ae(slug, { f: 'limit_wall_unreachable', why: reason, blocked: blocked ? 1 : 0 });
    }

    function showNote(slug, quiet, over) {
        var m = mounts[slug];
        if (!m || !m.notes.length) return;
        /* Отказ — СВОЙ исход, а не разновидность «бросил обработку». До этой
           метки 1182 ухода за 30 дней (13% корзины «stage:processing») были
           людьми, которым показали стену и которые ничего не запускали.
           Ставим до всех ранних выходов ниже: стена поднята — значит поднята. */
        try { window.__limitWallAt = window.__limitWallAt || Date.now(); } catch (_) {}
        m.noteVisible = true;
        m.noteOver = !!over;
        var blocked = isBlocked(slug);
        for (var i = 0; i < m.notes.length; i++) { paintNote(m.notes[i], blocked, over); show(m.notes[i]); }
        if (!quiet) adoptPressedButton(slug);
        paintButtons(slug, true);
        if (!quiet) pulseNote(slug);
        renderHint(slug);
        if (window.__announce) {
            try { window.__announce(noteLine(slug, blocked, over)); } catch (_) {}
        }
        /* Checked on the next frame: pulseNote() may have just scrolled the note
           into view, and layout has to settle before the question can be asked
           honestly. `quiet` reveals (an unverified account on arrival) are not
           answers to a press — nothing to escalate. */
        if (!quiet) {
            var check = function () {
                var why = noteUnreachableReason(slug);
                if (why) escalateUnreachable(slug, why, blocked, over);
                // A wall with no box in the window is not a wall anyone read —
                // the price on it cannot have done anything either way.
                else wallPriceSeen(slug, blocked);
            };
            if (typeof requestAnimationFrame === 'function') requestAnimationFrame(check);
            else setTimeout(check, 16);
        }
    }

    // Called on every begin(): a stale "limit reached" banner must not survive a
    // midnight rollover, a Pro purchase in another tab, or a cleared counter —
    // channel-converter shipped a banner that could only ever be shown.
    // EXCEPT for a blocked (unverified) account: nothing that happens on the page
    // can lift that state, so hiding the note would only make the tool look usable
    // again — the "banner that can only be shown" trap, in reverse.
    function hideNote(slug) {
        var m = mounts[slug];
        if (!m) return;
        if (isBlocked(slug)) { showNote(slug, true); return; }
        m.noteVisible = false;
        for (var i = 0; i < m.notes.length; i++) hide(m.notes[i]);
        paintButtons(slug, false);
        // The escalation toast is the wall speaking; when the wall comes down
        // (midnight, a purchase in another tab, a repeats-only queue) it has to
        // stop speaking too — an actionable toast has no auto-dismiss, so left
        // alone it would outlive the state it describes.
        dismissWallToast(slug);
    }
    function dismissWallToast(slug) {
        try {
            var t = document.querySelector('[data-tl-wall-toast="' + slug + '"] .toast-close');
            if (t) t.click();
        } catch (_) {}
    }

    /* An unverified account must learn the tool is closed BEFORE arranging a
       queue and pressing a dead button (the measured 2026-07-21 /join failure:
       21 presses, 0 CTA clicks, left). So the wall is shown as soon as the tier
       snapshot lands — quietly, no pulse, no scroll — on every mounted slug.
       Fires once per slug per page: `daily_limit_hit` must keep meaning "the
       user actually pressed". */
    var blockedSeen = Object.create(null);
    function enforceBlocked(slug) {
        if (!isBlocked(slug)) return;
        var m = mounts[slug];
        if (!m || !m.notes.length) return;
        showNote(slug, true);
        if (blockedSeen[slug]) return;
        blockedSeen[slug] = true;
        try { window.__ae && window.__ae('feature_use', { slug: slug, f: 'limit_tier_blocked' }); } catch (_) {}
    }

    /* ONE refresh for the whole block. The plan line and the extra ceilings ride
       the same cycle as the counter, so they cannot disagree about the tier —
       and they repaint together when the async tier snapshot lands. */
    function paintAll(slug) {
        renderHint(slug);
        renderPlan(slug);
        renderSnapshotDetails(slug);
        enforceBlocked(slug);
    }

    function refresh(slug) {
        if (slug) { paintAll(slug); return; }
        for (var s in mounts) paintAll(s);
    }

    /* ── run tokens ────────────────────────────────────────────────────────── */
    // fileKey (optional): the run is tied to a specific input FILE. A repeat
    // run of a file that already burned a slot today (same fingerprint in
    // state.f) is FREE — users iterate on one file (retry with another engine /
    // toggle; audio-to-notes: auto→solo→drums-off is 3 runs of 1 file), and a
    // cap sold as "N files/day" must count FILES, not clicks (Farid 2026-07-16).
    // Opt-in per call — tools that pass nothing keep exact legacy behavior.
    /* Токен — это ЕДИНСТВЕННОЕ место в коде сайта, где точно известно, что
       прогон начался и что он кончился: он выдаётся при нажатии на выдачу и
       гасится на КАЖДОМ выходе (шесть точек в /reverse, девять в /join). Ровно
       этого факта не хватало сборщику ухода — он выводил «идёт ли работа» из
       ИМЕНИ последней стадии и записывал 6608 прогревов за 30 дней в
       «брошено во время обработки». Публикуем факт здесь, один раз, для всех
       203 тулов сразу. См. window.__runMark в telemetry-tier0.js. */
    function markRunStart() {
        try { return (window.__runMark && window.__runMark.begin()) || null; } catch (_) { return null; }
    }
    function Token(slug, fileKey, isRepeat) {
        this.slug = slug; this.fileKey = fileKey || null; this.repeat = !!isRepeat;
        this.settled = false;
        this._endRun = markRunStart();
    }
    Token.prototype._closeRun = function () {
        if (this._endRun) { try { this._endRun(); } catch (_) {} this._endRun = null; }
    };
    Token.prototype.commit = function () {
        if (this.settled) return;            // one token = at most one run
        this.settled = true;
        this._closeRun();
        if (!this.repeat) API.record(this.slug, this.fileKey);
        renderHint(this.slug);
    };
    Token.prototype.abort = function () {
        this.settled = true;                 // a late commit() from an unabortable job is now a no-op
        this._closeRun();
        // The result is thrown away (a new export replaces it): so is its rescue copy.
        try { if (this._kept && window.__exportKeepHas && window.__exportKeepHas(this._kept) && window.__exportKeepDrop) window.__exportKeepDrop(); } catch (_) {}
        this._kept = null;
    };
    /* The work is done and its result waits for the person to take it. The run
       bracket closes HERE — nothing runs any more, so a reload must not report
       a run «lost», and the screen need not stay awake for a press — while the
       unit stays open for commit() (delivered) or abort() (thrown away).
       awaitDelivery({blob, name}) — the result is a file: it is kept (export-keep,
       timbrica-core.js) until it is taken, so a tab that dies before the press leaves
       it for the restore bar on the next page view; a person who leaves without it
       chose so (untilLeave). Support mail 16.10.2026 and refund #48 (/audio-to-video,
       a rendered clip nobody took): the result lived only as long as the page. */
    Token.prototype.awaitDelivery = function (result) {
        this._closeRun();
        try {
            if (result && result.blob && result.blob.size && window.__exportKeepSave) {
                window.__exportKeepSave(result.blob, result.name || 'download', { untilLeave: true });
                this._kept = result.blob;
            }
        } catch (_) {}
    };

    var API = {
        cap: cap,
        // Derived view-state for the counter + bar (level/ratio). Exposed so the
        // UI, tests and any bespoke blade read ONE source of truth.
        state: usageState,
        /* Publish ONE tier-limited fact about this tool into the shared block.
         *
         *   __toolLimit.setDetail('audio-to-video', 'quality:height',
         *                         'Экспорт до 720p · Премиум: до 2160p');
         *
         * For modules that own a ceiling of their own — quality-tier.js is the
         * first caller. `key` dedupes, so re-gating updates the line instead of
         * appending a second copy; passing an empty text retracts it.
         *
         * The point is that a tool never grows its own upsell banner: every
         * "your plan is smaller than this" sentence lands in the same box, in
         * one order, with one CTA underneath. */
        setDetail: setDetail,
        /* "The visitor's CURRENT selection runs on the server" — toggled by the
         * page (convert/file-converter flip it when a server-badged format is
         * picked). Shows the blade-rendered server-quota line («:n в день,
         * Премиум — до :paid») only while true and only to non-Pro viewers; a
         * browser-lane selection retracts it, so the 95% whose files never
         * leave the browser are not warned about a limit they cannot meet. */
        serverLaneCase: function (slug, active) {
            var m = mounts[slug];
            if (!m || !m.roots || !m.roots.length) return;
            var txt = (active && !API.isPro())
                ? (m.roots[0].getAttribute('data-tl-detail-serverlane') || '')
                : '';
            setDetail(slug, 'serverlane', txt);
        },
        isPro: function () { return !!(window.__tierLimits && window.__tierLimits.is_pro); },
        // "Is this capped tool closed for this account?" — true only for an
        // unverified email (server flag caps_blocked + a cap entry for the slug).
        // Exposed so a tool with its own extra gate (join's file-count cap,
        // document-reader's char gate) can ask the same question this module does.
        blocked: isBlocked,
        used: function (slug) { return usedOf(slug); },
        remaining: function (slug) { var c = cap(slug); return c === Infinity ? Infinity : Math.max(0, c - usedOf(slug)); },
        /**
         * The SERVER's count for a slug whose unit is spent on our server
         * (see serverCounts above). Hand over every answer that carries it:
         *   __toolLimit.serverCount('anglicism-checker', d.quota);   // {cap, used}
         * The counter and the bar follow it; a wall the new number no longer
         * supports (midnight, a purchase) comes down by itself.
         */
        serverCount: function (slug, q) {
            if (!slug || !q || typeof q.cap !== 'number') return;
            serverCounts[slug] = { cap: q.cap | 0, used: Math.max(0, q.used | 0) };
            var m = mounts[slug];
            if (m && m.noteVisible && API.remaining(slug) > 0) hideNote(slug);
            renderHint(slug);
        },
        /**
         * The server refused the unit (its day is spent): the same wall a
         * refused begin() raises — note at the button, pulse, bold line, the
         * toast when the note cannot be seen. q (optional) — the count it sent.
         */
        serverRefused: function (slug, q) {
            if (q && typeof q.cap === 'number') {
                serverCounts[slug] = { cap: q.cap | 0, used: Math.max(0, q.used | 0) };
            }
            showNote(slug);
            try {
                window.__ae && window.__ae('feature_use', {
                    slug: slug, f: 'daily_limit_hit', server: 1,
                    cap: API.cap(slug) === Infinity ? 0 : API.cap(slug), used: API.used(slug), need: 1
                });
            } catch (_) {}
        },
        // Is the NEXT run allowed? (Infinity cap → always true.)
        allowed: function (slug) { return this.remaining(slug) > 0; },
        // Cheap synchronous fingerprint of a File/Blob for the unique-file
        // counting below. name+size+lastModified: survives a re-pick of the
        // same file from disk; a renamed copy counts as new (acceptable); mic
        // takes get a fresh lastModified each time — each recording rightly
        // counts as its own file. No content read — begin() must stay sync.
        fileKey: function (file) {
            if (!file) return null;
            var k = rawKey(file);
            /* A part cut on this page from a recording the device could not hold
               whole bills as that RECORDING (audio-parts.js, bound 'device'): the
               split is our limitation, and one recording is one unit — as it is on
               a machine with more memory. A part of a TIER-bound split keeps its
               own key: there the per-file length is what the plan sells. */
            try {
                var P = window.AudioParts;
                var src = P && typeof P.sourceKeyFor === 'function' ? P.sourceKeyFor(k) : null;
                if (src) return src;
            } catch (_) {}
            return k;
        },
        zoneFor: zoneFor,
        partsPlan: partsPlan,
        // How the duration dialogs print a length — exposed for its test.
        fmtDur: fmtDur,
        // When the counter + Premium line goes under the upload zone — exposed for its test.
        zoneLineWanted: zoneLineWanted,
        // The ONE :remaining / :cap fill of every limit sentence — exposed for its test.
        fillText: fillLimitText,
        _fillNode: fillLimitNode,
        _syncZoneLine: syncZoneLine,
        // Record ONE successful run. Prefer begin()/commit() — this is the raw
        // primitive, with no idempotency and no cancel awareness.
        // fileKey (optional): remember which FILE burned this slot, so later
        // runs of the same file today are free (see begin()). Deduped; the
        // list can never exceed the cap (only counted runs enter it).
        // Build keys for a BATCH (multi-file tools like /join). Deduped.
        fileKeys: function (files) {
            var out = [];
            try {
                for (var i = 0; i < ((files && files.length) || 0); i++) {
                    // A ready key — the composite 'set:…' of a tool whose unit is the
                    // OPERATION (/join, /merge-pdf, /video-merge) — passes as is, so
                    // precheck() can ask «does this merge fit?» instead of counting
                    // its files. A string has no name/size, so fileKey() would have
                    // turned every one into the same '|0|0'.
                    var k = typeof files[i] === 'string' ? files[i] : this.fileKey(files[i]);
                    if (k && out.indexOf(k) === -1) out.push(k);
                }
            } catch (_) {}
            return out;
        },
        // Which of these keys have NOT been counted today (and are unique in the
        // batch) — i.e. what a run would actually cost.
        //
        // ⚠️ Membership goes through a SET, not Array.indexOf. Both sides scale
        // with the tool's own cap: /ticket-generator bills one unit per TICKET
        // and its Премиум ceiling is 20 000, so the day's remembered list and
        // the incoming batch are both that long. Linear scan made the pair
        // O(n·m) — measured on the real key shape, 20 000 × 20 000 is ~400M
        // string comparisons and freezes the tab for minutes INSIDE a click
        // handler, which looks exactly like a hung export. The set is O(n+m)
        // and the returned order is unchanged, so every caller sees the same
        // result. (heic-converter at 2 000/day was on the same curve.)
        freshKeys: function (slug, keys) {
            var fresh = [];
            try {
                var seen = makeKeySet((readState(slug).f) || []);
                var paid = pageKeys(slug);
                for (var i = 0; i < ((keys && keys.length) || 0); i++) {
                    var k = keys[i];
                    if (!k || seen.has(k) || paid.has(k)) continue;
                    seen.add(k);
                    fresh.push(k);
                }
            } catch (_) {}
            return fresh;
        },
        record: function (slug, fileKey) {
            try {
                var s = readState(slug);
                var keys = fileKey == null ? [] : (Object.prototype.toString.call(fileKey) === '[object Array]' ? fileKey : [fileKey]);
                var fresh = [];
                var seenRec = makeKeySet(s.f || []);   // set, not indexOf — see freshKeys
                for (var i = 0; i < keys.length; i++) {
                    var k = keys[i];
                    if (!k || seenRec.has(k)) continue;
                    seenRec.add(k);
                    fresh.push(k);
                }
                // With keys → bill ONE UNIT PER NEW FILE, so a 10-file merge spends
                // ten of the day's file budget (Farid 2026-07-18: "выбрал 10 файлов
                // и сохранил → потратил лимит за день"). Without keys → the legacy
                // one-press-one-unit behaviour every other tool still uses.
                var cost = keys.length ? fresh.length : 1;
                var paidHere = pageKeys(slug);
                for (var pk = 0; pk < keys.length; pk++) if (keys[pk]) paidHere.add(keys[pk]);
                if (cost <= 0) return this.remaining(slug); // all files already counted today
                s.n = (s.n | 0) + cost;
                s.d = dayStamp();
                if (fresh.length) {
                    s.f = (s.f || []).concat(fresh);
                    // Bound the remembered-file list BY THE TOOL'S OWN CAP (never
                    // below 150). A fixed 150 was "above every cap" only while every
                    // cap was ≤150: with heic-converter's paid 2000 files/day it would
                    // forget files the user is still entitled to re-convert for free
                    // today, silently billing them twice.
                    var capN = cap(slug);
                    var bound = Math.max(150, capN === Infinity ? 150 : capN);
                    if (s.f.length > bound) s.f = s.f.slice(-bound);
                }
                localStorage.setItem(key(slug), JSON.stringify(s));
                /* Which of the day's units this was — the ONE report of a count that
                   lives only in this page. A refund needs it: a file past the free
                   tiers' cap is a result that only Premium could give (refund policy
                   п. 1.5.1, edition 21.10.2026; RenderedPaidService compares `n` with
                   the cap and pairs it with a delivery). Refund request №50: six
                   files past a cap of one, refunded in full on the day of purchase,
                   because nothing on the server knew they were past it. Paid
                   accounts only — a free one cannot pass its own cap, so its spends
                   would prove nothing and only fill the log. */
                if (this.isPro()) ae(slug, { f: 'limit_spend', n: s.n, cost: cost });
            } catch (_) {}
            return this.remaining(slug);
        },

        /**
         * Start a run. Returns null when the daily cap is spent — the mounted
         * note is revealed and daily_limit_hit telemetry fires, so the caller
         * only has to `return`.
         * fileKey (optional, via API.fileKey(file)): a repeat run of a file
         * that already burned a slot TODAY is allowed even at the cap and does
         * not count again — "N files/day" counts files, not clicks.
         */
        begin: function (slug, fileKey) {
            // fileKey may be ONE key (single-file tools) or an ARRAY (a batch, e.g.
            // /join): the run then costs one unit per NEW file, so a 10-file merge
            // spends ten of the day's file budget instead of a single press.
            // No key at all → ask the page what is loaded (see selectionKey).
            var keyless = fileKey == null;
            if (fileKey == null) fileKey = selectionKey();
            var keys = fileKey == null ? [] : (Object.prototype.toString.call(fileKey) === '[object Array]' ? fileKey : [fileKey]);
            var fresh = keys.length ? this.freshKeys(slug, keys) : [];
            var cost = keys.length ? fresh.length : 1;
            var isRepeat = keys.length > 0 && cost === 0;   // every file already counted today
            var rem = this.remaining(slug);                 // Infinity for paid tiers
            // Unverified account: refuse unconditionally — including the
            // repeat-file path, which is normally free. Its whole premise is "you
            // already spent a slot on this file today", and a closed tier never
            // spent one (a counter left over from before the account was walled
            // would otherwise reopen the tool).
            if (isBlocked(slug)) {
                showNote(slug);
                try {
                    window.__ae && window.__ae('feature_use', { slug: slug, f: 'daily_limit_hit', cap: 0, used: this.used(slug), need: cost, blocked: 1 });
                } catch (_) {}
                return null;
            }
            if (!isRepeat && rem < cost) {
                // What the refusal was about — a later pick of a file already
                // counted today may lift it (reconsiderWallAfterPick).
                var wm = slot(slug);
                wm.wallKeys = keys.slice(0);
                wm.wallKeyless = keyless;
                showNote(slug, false, rem > 0);   // budget left, queue too big ≠ spent out
                // Судили гостевыми числами до снимка — пересудим, когда он приедет.
                if (!window.__tierLimits) slot(slug).provisionalNeed = cost;
                try {
                    window.__ae && window.__ae('feature_use', {
                        slug: slug, f: 'daily_limit_hit',
                        cap: this.cap(slug), used: this.used(slug), need: cost
                    });
                } catch (_) {}
                return null;
            }
            if (isRepeat) {
                // The page now holds a paid file — the day's list may lose it
                // at midnight, the page does not.
                try { var ph = pageKeys(slug); for (var pp = 0; pp < keys.length; pp++) if (keys[pp]) ph.add(keys[pp]); } catch (_) {}
                // Free only because this page paid for it on an EARLIER day:
                // how often the midnight carry decides (see pagePaid).
                try {
                    var today = makeKeySet((readState(slug).f) || []), carried = 0;
                    for (var ck = 0; ck < keys.length; ck++) if (keys[ck] && !today.has(keys[ck])) carried++;
                    if (carried) window.__ae && window.__ae('feature_use', { slug: slug, f: 'limit_page_carry', n: carried });
                } catch (_) {}
            }
            if (isRepeat && rem <= 0) {
                // The free-rerun path actually saved this run from the wall —
                // worth counting: it says how often iteration-on-one-file was
                // being punished before unique-file counting shipped.
                try { window.__ae && window.__ae('feature_use', { slug: slug, f: 'limit_free_rerun' }); } catch (_) {}
            }
            hideNote(slug);
            renderHint(slug);   // the note may have been covering it
            return new Token(slug, keys.length ? fresh : fileKey, isRepeat);
        },

        /**
         * What a press would bill RIGHT NOW — the same keys the tool will pass to
         * begin() (a multi-input tool: its composite 'set:…' key). Optional and
         * additive: without it nothing changes. With it, a counter at «0 из N» over
         * a selection already counted today says that the repeat is free
         * (data-tl-repeat, `limit_repeat_text` through the tool's wording chain).
         * null / [] — nothing is loaded.
         */
        pending: function (slug, keys) {
            var m = slot(slug);
            m.pendingKeys = (keys && keys.length) ? keys.slice(0) : null;
            renderHint(slug);
        },

        /**
         * `asKey` names the unit `paidKey` already paid for: the tool knows they
         * are one piece of work although the bytes differ. Written for an
         * editor's own RESULT coming back as input — bug report 27.10.2026
         * (/pdf-editor, cap 1): save the document, open the saved file to fix a
         * typo, and the day's one document was billed a second time (RU
         * 23–27.10: 6 of 81 walled sessions had re-opened, byte for byte, the
         * file the editor had just given them). Costs nothing, and joins nothing
         * that was not paid: a paidKey absent from today's list and from this
         * page returns false and changes no state.
         */
        sameUnit: function (slug, paidKey, asKey) {
            if (!paidKey || !asKey || paidKey === asKey) return false;
            try {
                var s = readState(slug);
                var today = makeKeySet(s.f || []);
                var here = pageKeys(slug);
                var paidToday = today.has(paidKey), paidHere = here.has(paidKey);
                if (!paidToday && !paidHere) return false;
                if (paidHere) here.add(asKey);
                if (paidToday && !today.has(asKey)) {
                    s.f = (s.f || []).concat([asKey]);
                    var capN = cap(slug);
                    var bound = Math.max(150, capN === Infinity ? 150 : capN);
                    if (s.f.length > bound) s.f = s.f.slice(-bound);
                    localStorage.setItem(key(slug), JSON.stringify(s));
                }
                renderHint(slug);
                return true;
            } catch (_) { return false; }
        },

        /**
         * Register limit UI for a slug. Additive — call it once per note/hint
         * pair. Ids or nodes both work, so a legacy blade can hand over the
         * markup it already has.
         *   mount('gif-compressor', { note:'gcLimitNote', hint:'gcLimitHint',
         *                             hintText:'gcLimitHintText', template: '...' })
         */
        mount: function (slug, opts) {
            opts = opts || {};
            var m = slot(slug);
            var note = byId(opts.note);
            var hint = byId(opts.hint);
            var root = byId(opts.root);
            // Список ID через запятую, а не один: у тула бывает НЕСКОЛЬКО кнопок
            // выдачи (у /photo-to-gif их две — GIF и WebM), и та, что не помечена,
            // остаётся на вид рабочей. Ровно это и стоило сессии на /join
            // 21 нажатие в неотвечающую кнопку. m.buttons и paintButtons уже
            // умеют массив — не хватало только разбора.
            var buttons = String(opts.button || '').split(',')
                .map(function (s) { return byId(s.trim()); })
                .filter(Boolean);

            if (root && m.roots.indexOf(root) < 0) m.roots.push(root);
            if (note && m.notes.indexOf(note) < 0) { note.__tlSlug = slug; hide(note); m.notes.push(note); }
            if (hint && !m.hints.some(function (h) { return h.el === hint; })) {
                m.hints.push({ el: hint, text: byId(opts.hintText), template: opts.template || '', repeat: opts.repeat || '', repeatAny: opts.repeatAny || '' });
            }
            for (var bi = 0; bi < buttons.length; bi++) {
                if (m.buttons.indexOf(buttons[bi]) < 0) {
                    m.buttons.push(buttons[bi]);
                    if (m.noteVisible) paintButtons(slug, true); // late mount while walled
                }
            }
            renderHint(slug);
            enforceBlocked(slug);   // unverified account: the wall goes up on arrival
            watchExposure(slug);    // …and start counting whether anyone SEES it
            return m;
        },

        /**
         * Early wall — call when the QUEUE changes (file added/removed), not only
         * at press time. The 2026-07-21 walled user arranged 9 fresh tracks with
         * the "0 из 10" hint sitting below the fold and learned about the wall
         * only from a dead-feeling button. precheck(files):
         *   over budget  → shows the note (+ blocked button) NOW, returns false
         *   affordable   → clears a stale note, returns true
         * Fires a light `limit_precheck_block` (not daily_limit_hit — that metric
         * must keep meaning "the user actually pressed"). No billing here.
         */
        precheck: function (slug, files) {
            /* Гонка со снимком тиров. precheck синхронный — обязан ответить
               сразу, — а потолок живёт в window.__tierLimits, который приезжает
               асинхронным /api/session-bootstrap: замер на проде 2026-08-09 дал
               1.7–3.3 с после загрузки. До снимка cap() = Infinity (с 16.09.2026 —
               только у вошедшего: гостя судит гостевая карта layout, см. tiers()), поэтому
               ЛЮБАЯ очередь «влезает», записка не поднимается и больше не
               поднимется — сам себя precheck не переигрывает (renderHint,
               в отличие от него, переподписан на tier-bootstrap:ready).

               Учёт при этом не течёт: begin() при нажатии всё равно остановит.
               Теряется ровно раннее предупреждение, ради которого precheck и
               написан, — человек ставит очередь, жмёт и получает часть файлов.

               Кого касается — ЗАМЕРЕНО, а не выведено из кода (сначала я записал
               «касается всех» и ошибся): тот, кто после выбора делает
               асинхронную работу и по её завершении перерисовывается, зовёт
               precheck повторно и чинится сам — так ведёт себя /join. Тот, кто
               зовёт его один раз прямо в обработчике выбора, теряет
               предупреждение навсегда: heic-converter, photo-enhancer,
               photo-repair, remove-metadata (4 из 5, прогон
               tests/playwright/precheck-bootstrap-race.mjs).

               Поэтому запоминаем аргументы ТОЛЬКО когда снимка ещё нет, и
               переигрываем один раз, когда он приедет. Синхронный контракт не
               меняется: вызывающий как получал мгновенный ответ, так и получает. */
            if (!window.__tierLimits) pendingPrecheck[slug] = files || [];
            if (isBlocked(slug)) { enforceBlocked(slug); return false; }
            var keys = this.fileKeys(files || []);
            var fresh = this.freshKeys(slug, keys);
            var rem = this.remaining(slug);
            if (fresh.length > 0 && rem < fresh.length) {
                var m = slot(slug);
                var already = m.noteVisible;
                // rem > 0 ⇒ nothing is spent yet, the queue simply does not fit.
                showNote(slug, false, rem > 0);
                if (!already) {
                    try { window.__ae && window.__ae('feature_use', { slug: slug, f: 'limit_precheck_block', need: fresh.length, cap: this.cap(slug) === Infinity ? 0 : this.cap(slug) }); } catch (_) {}
                }
                return false;
            }
            hideNote(slug);
            renderHint(slug);
            return true;
        },

        // Dismiss a shown note without starting a run — e.g. the user cleared the
        // queue or picked a new file. begin() also hides it, but a note that can
        // only ever be SHOWN outlives a midnight rollover and a Pro purchase.
        clearNote: function (slug) { hideNote(slug); renderHint(slug); },

        /**
         * This viewer's ceiling on INPUT DURATION for `slug`, in seconds.
         * 0 = none configured (fail-open — same rule as cap()): that is how a
         * guest arrives on a locale where Премиум is not sold, and putting a paid
         * wall where nothing can be bought is forbidden.
         */
        durationCap: durationCap,

        /**
         * Pre-flight duration gate — the ONLY place a tier duration wall is drawn.
         *
         * Returns a Promise<boolean>: true ⇒ the file fits, run it; false ⇒ walled
         * (the dialog already ran, telemetry already fired, the caller just returns).
         *
         *   if (!await window.__toolLimit.checkDuration('denoise', durS)) return;
         *
         * opts.deviceCeilS — what THIS device can actually chew in one pass, when
         *   the tool knows it. It is what keeps the wall honest: the upgrade is
         *   offered ONLY when the file fits in min(device, Премиум). Selling
         *   Премиум for a file that dies on the same laptop afterwards is selling
         *   a known failure — measured on /enhance-video, where files over 20 min
         *   finish 18.6% of the time with ≥8 GB of RAM just as without it.
         * opts.splitUrl — where "split it" leads (default /chapter-splitter).
         *
         * ⚡ Waits for the tier snapshot before judging: window.__tierLimits is
         * assigned only by the async session bootstrap, so a file dropped in the
         * first ~200 ms would otherwise sail past every ceiling. Bounded wait,
         * then fail-open.
         */
        checkDuration: function (slug, durS, opts) {
            opts = opts || {};
            return tierSnapshotReady(3000).then(function () {
                var tier = durationCap(slug);
                var device = opts.deviceCeilS > 0 ? opts.deviceCeilS : 0;
                var effective = tier > 0 ? (device > 0 ? Math.min(device, tier) : tier) : device;
                // Fits every ceiling — the one question left is whether THIS device
                // can be expected to finish it (see phoneAdvisory above).
                if (!durS || !effective || durS <= effective) return phoneAdvisory(slug, durS, opts);
                // Past the ceiling. Whether we may SELL here is a separate question
                // from whether we must stop: the upgrade only lifts the tier bound.
                var prem = premiumDurationCap(slug);
                var afterUpgrade = prem > 0 ? (device > 0 ? Math.min(device, prem) : prem) : device;
                var sellable = !API.isPro() && tier > 0 && durS <= afterUpgrade && afterUpgrade > tier;
                // WHICH ceiling binds decides the sentence. The tier one is a limit
                // the plan sets; the device one is what this browser can hold, and
                // no plan lifts it. Until 2026-09-12 the dialog always printed the
                // tier number — on a tool without a tier cap that is 0, so the
                // first device ceiling ever passed in would have read «лимит — 0».
                var bound = (tier > 0 && (device <= 0 || tier <= device)) ? 'tier' : 'device';
                // The file itself (opts.file, passed by AudioDecoderRobust.decodeGated
                // and the default whole-buffer gate) is what makes in-place parts
                // possible; a caller without it keeps the old handoff.
                var file = opts.file || null;
                var parts = canCutInPlace(file) ? partsPlan(durS, effective) : null;
                if (parts) loadParts();   // while the person reads the dialog
                ae(slug, { feature: 'tier_duration_wall', dur_s: Math.round(durS), tier_s: tier, premium_s: prem,
                           device_s: device, bound: bound, sellable: sellable ? 1 : 0, parts: parts ? parts.n : 0 });
                return durationDialog(slug, durS, effective, prem, sellable, opts, bound, parts).then(function (choice) {
                    ae(slug, { feature: 'tier_duration_wall', src: 'choice', action: choice, parts: parts ? parts.n : 0 });
                    if (choice === 'pro') {
                        location.href = window.__PRO_URL || ('/' + (window.__LOCALE || 'en') + '/pricing');
                        return false;
                    }
                    if (choice === 'split') {
                        if (parts) {
                            markGateSpoke(slug, 'parts');
                            startParts({ slug: slug, file: file, durS: durS, targetS: effective, bound: bound, plan: parts });
                            return false;
                        }
                        location.href = opts.splitUrl || ('/' + (window.__LOCALE || 'en') + '/chapter-splitter');
                        return false;
                    }
                    markGateSpoke(slug, 'cancel');
                    return false;
                });
            });
        },

        /**
         * Pre-flight choice for work whose real cost is only knowable ONCE THE
         * DEVICE HAS BEEN TIMED — the AI lanes, where the same clip is twenty
         * minutes on one GPU and ten hours on another (prod, 213 completed
         * /enhance-video AI runs: per-tile p50 89 ms, p90 285, max 3563).
         *
         *   if (!await window.__toolLimit.confirmLongRun('enhance-video', sec)) return;
         *
         * Not a wall and not a tier question: nothing is for sale here, the
         * device is simply slow. It is the same pre-flight courtesy the phone
         * advisory pays — the number, before the wait, once per run — drawn by
         * the same modal so a tool never grows its own.
         *
         * Born from comment #1645 (12.09.2026): ten hours on a three-minute
         * clip, the honest figure available at minute one and shown to nobody.
         * Declining is a real answer: the caller must abort its cap token.
         */
        confirmLongRun: function (slug, sec, opts) {
            opts = opts || {};
            if (!(sec > 0)) return Promise.resolve(true);
            var m = mounts[slug];
            var root = m && m.roots && m.roots[0];
            var str = function (k, fallback) {
                var v = root && root.getAttribute('data-tl-' + k);
                return v || fallback;
            };
            var dur = fmtDur(sec, function (k, fb) { return str('dur-' + k, fb); });
            var body = str('longrun-body',
                'On this device the run will take about :t. The tab has to stay open and awake the whole time — a phone or a closed laptop will not finish it. A smaller output resolution, or a shorter clip, takes proportionally less.')
                .replace(/:t\b/g, dur);
            ae(slug, { feature: 'long_run_advisory', eta_s: Math.round(sec), src: opts.src || '' });
            return modalChoice(slug, '⏳ ' + str('longrun-title', 'This will take a while'), body, [
                { id: 'continue', primary: true, label: str('longrun-continue', 'Start anyway') },
                { id: 'cancel', label: str('longrun-cancel', 'Cancel') }
            ], 'data-tl-longrun-dialog').then(function (choice) {
                ae(slug, { feature: 'long_run_advisory', src: 'choice', action: choice, eta_s: Math.round(sec) });
                return choice === 'continue';
            });
        },

        refresh: refresh
    };

    window.__toolLimit = API;

    /* Server-lane per-file ceiling in MB for a tool, admin-overridable on
     * /pino/server-lane and delivered via __tierLimits.server_lane_mb (the
     * same resolver the gate and the handlers' validators use). The pages'
     * own prechecks call this instead of baking 30/50-MB constants, so a
     * raised Premium ceiling works through the UI too. `fallbackMb` is the
     * page's historical constant — used before the bootstrap resolves, so
     * degraded behaviour is exactly the old behaviour. */
    window.__serverLaneMb = function (slug, fallbackMb) {
        var m = window.__tierLimits && window.__tierLimits.server_lane_mb;
        var v = m && m[slug];
        return (typeof v === 'number' && v > 0) ? v : fallbackMb;
    };

    /* ── auto-mount + bootstrap race ───────────────────────────────────────────
       window.__tierLimits is assigned only inside the async sessionBootstrap
       handler in layouts/app.blade.php (and its terminal fallback), which then
       dispatches tier-bootstrap:ready. A tool that renders its hint once at
       parse time therefore reads cap()===Infinity and hides the hint forever —
       repair-audio shipped exactly that bug. Re-render on the event, and keep a
       late timer for the fallback path. */
    function autoMount() {
        var roots = document.querySelectorAll('[data-tool-limit]');
        for (var i = 0; i < roots.length; i++) {
            var root = roots[i];
            var slug = root.getAttribute('data-tool-limit');
            if (!slug) continue;
            // mount() is additive and de-dupes by node, so re-running autoMount
            // (DOMContentLoaded, tier-bootstrap:ready, late timer) is a no-op.
            API.mount(slug, {
                root: root,
                note: root.querySelector('[data-tl-note]'),
                hint: root.querySelector('[data-tl-hint]'),
                hintText: root.querySelector('[data-tl-hint-text]'),
                template: root.getAttribute('data-tl-template') || '',
                // «этот набор сегодня уже учтён» — see API.pending
                repeat: root.getAttribute('data-tl-repeat') || '',
                // «учтённое сегодня можно обработать снова» — see renderHint
                repeatAny: root.getAttribute('data-tl-repeat-any') || '',
                // Opt-in: the tool's action button answers the wall itself
                // (dim + aria-disabled while the note is up). By ELEMENT ID.
                button: root.getAttribute('data-tl-button') || null,
                // Opt-in: name the paid ceiling from the first visit, not only
                // near the wall (see hintIsActionable).
            });
        }
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { autoMount(); bindUploaders(); mountZoneLines(); });
    else { autoMount(); bindUploaders(); mountZoneLines(); }

    /* ── one-time limit grants (support gifts) ─────────────────────────────
       The daily counter lives in THIS device's localStorage, so support can't
       reset it server-side — instead sessionBootstrap carries `limit_grants`
       ([{id, slug}], Cache-backed, short TTL) and the client applies each
       grant exactly once: zero today's counter for the slug, then mark the
       grant id consumed (localStorage `tulg:<id>`). Re-running is a no-op —
       the counter fills up again normally, tomorrow's rollover unaffected. */
    function applyGrants() {
        try {
            var grants = (window.__tierLimits && window.__tierLimits.limit_grants) || [];
            for (var i = 0; i < grants.length; i++) {
                var g = grants[i];
                if (!g || !g.id || !g.slug) continue;
                var mark = 'tulg:' + g.id;
                if (localStorage.getItem(mark)) continue;      // already applied once
                localStorage.setItem(key(g.slug), JSON.stringify({ d: dayStamp(), n: 0 }));
                localStorage.setItem(mark, dayStamp());
                hideNote(g.slug);
                renderHint(g.slug);
            }
        } catch (_) {}
    }

    // syncServerClock ПЕРВЫМ: applyGrants пишет, а refresh читает состояние,
    // датированное dayStamp() — обе операции должны видеть уже серверный день.
    /* Переигровка ранней стены. Очереди, про которые precheck судил без снимка
       тиров, судились при cap() = Infinity и всегда «влезали»; теперь потолок
       известен — спрашиваем заново. Идёт ПОСЛЕ applyGrants: подарок лимита
       обнуляет счётчик, и переигрывать надо уже с ним, иначе покажем стену
       тому, кому её только что сняли. */
    function replayPendingPrecheck() {
        var slugs = Object.keys(pendingPrecheck);
        for (var i = 0; i < slugs.length; i++) {
            var s = slugs[i], files = pendingPrecheck[s];
            delete pendingPrecheck[s];                 // ровно один раз
            try { API.precheck(s, files); } catch (_) {}
        }
    }
    /* Стена, поднятая ГОСТЕВЫМИ числами до снимка (см. tiers()), — суждение
       предварительное. Настоящий снимок вправе сказать иначе: вошедший без
       метки входа получил гостевую копию страницы и судился как гость, а у него
       Премиум. Такую стену снимаем, если настоящий запас покрывает то, о чём
       просили; стену, которую подтверждает и настоящий снимок, не трогаем.
       Без снимка — ничего не делает и ничего не забывает. */
    function rejudgeProvisionalWalls() {
        if (!window.__tierLimits) return;
        for (var s in mounts) {
            var m = mounts[s], need = m.provisionalNeed | 0;
            m.provisionalNeed = 0;
            if (need > 0 && m.noteVisible && !isBlocked(s) && API.remaining(s) >= need) {
                hideNote(s);
                renderHint(s);
            }
        }
    }
    document.addEventListener('tier-bootstrap:ready', function () { syncServerClock(); autoMount(); bindUploaders(); mountZoneLines(); applyGrants(); refresh(); replayPendingPrecheck(); rejudgeProvisionalWalls(); });
    setTimeout(function () { syncServerClock(); autoMount(); bindUploaders(); mountZoneLines(); applyGrants(); refresh(); replayPendingPrecheck(); rejudgeProvisionalWalls(); }, 1600);
})();
